import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  Menu,
  Tooltip,
  Divider,
  TableSortLabel,
  Card,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
  FormControl,
  Breadcrumbs,
  Stack,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  RefreshIcon,
  SearchIcon,
  CloseIcon,
  CalendarIcon,
  ViewListIcon,
  ViewModuleIcon,
  SettingsIcon,
} from "../../constants/icons";
import { AlertHistorySettings } from "../../components/alertServicesSections/AlertHistorySettings";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrow as PlayArrowIcon,
  Image as ImageIcon,
  History as HistoryTabIcon,
  Tune as TuneIcon,
  Videocam as VideocamIcon,
  MoreVert as MoreVertIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useAlertData } from "../../contexts/AlertDataContext";
import {
  alertsHistoryAPI,
  useCasesAPI,
  camerasAPI,
  zonesAPI,
} from "../../services/api";
import { EmptyState } from "../../components/alertServicesSections/EmptyState";
import {
  AlertHistoryMediaImage,
  AlertHistoryMediaVideo,
} from "../../components/alertServicesSections/AlertHistoryAuthenticatedMedia";
import {
  getMediaFieldsFromAlertApiPayload,
  buildAlertHistoryCarouselSlides,
} from "../../utils/alertApiPayloadMedia";
import {
  detectionMediaPath,
  resolveServiceworkerMediaPath,
} from "../../utils/alertDetectionMedia";
import {
  formatAlertNotifiedToDisplay,
  resolveAlertTypeNameRaw,
  isAlertHistoryUsecaseId,
  resolveDashboardRowUseCaseId,
  parseAlertApiDateTime,
} from "../../utils/alertStreamUtils";
import AlertNotifiedByChips from "../../components/alertServicesSections/AlertNotifiedByChips";
import {
  normalizeNotifyChannelStrings,
  notifyChannelsFromAlertPayload as notifyChannelsFromAlert,
  buildNotifyChannelItems,
} from "../../utils/alertNotifyChannels";
import { readSrIdFromUnknown } from "../../utils/srIdDisplay.ts";

const HISTORY_TABLE_HEADER_SX = {
  fontFamily: "Roboto, sans-serif",
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "#2932E5",
  backgroundColor: "#fafafa",
};

/** Tune filter menu: fixed row layout; label column shrinks/wraps (URLs) so panel width stays stable. */
const HISTORY_FILTER_MENU_ITEM_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.75,
  py: 0.75,
  pl: 2,
  pr: 1,
  maxWidth: "100%",
  boxSizing: "border-box",
};

const HISTORY_FILTER_MENU_LABEL_SX = {
  flex: "1 1 auto",
  minWidth: 0,
  fontFamily: "Roboto, sans-serif",
  fontSize: "0.875rem",
  lineHeight: 1.4,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const HISTORY_FILTER_MENU_CHECKBOX_SX = {
  pt: 0.15,
  flexShrink: 0,
  alignSelf: "flex-start",
};

/** Fixed width so the menu does not grow/shrink when different sections load (use cases, etc.). */
/** Toolbar filter chips: Today, Sub-source, Zone, Alert Type (title case labels). */
function historyToolbarFilterButtonSx(menuOpen) {
  const open = Boolean(menuOpen);
  return {
    fontWeight: 400,
    lineHeight: "1.4375em",
    letterSpacing: "0.00938em",
    color: "rgba(0, 0, 0, 0.87)",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
    backgroundColor: "white",
    borderRadius: "10px",
    height: "36px",
    minWidth: "132px",
    maxWidth: "220px",
    paddingLeft: "14px",
    paddingRight: "12px",
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    textTransform: "none",
    border: open ? "1px solid #2932E5" : "1px solid #e0e0e0",
    "& .MuiButton-endIcon": { marginLeft: "4px", marginRight: 0 },
    "& .MuiButton-label": {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      flex: 1,
      justifyContent: "flex-start",
    },
    "&:hover": {
      backgroundColor: "white",
      borderColor: open ? "#2932E5" : "#bdbdbd",
    },
    "&:focus": { border: "1px solid #2932E5", outline: "none" },
    "&.Mui-focusVisible": { border: "1px solid #2932E5", outline: "none" },
  };
}

const HISTORY_FILTER_MENU_PAPER_SX = {
  width: "min(320px, calc(100vw - 24px))",
  minWidth: "min(320px, calc(100vw - 24px))",
  maxWidth: "min(320px, calc(100vw - 24px))",
  boxSizing: "border-box",
  maxHeight: "calc(100vh - 88px)",
  overflowY: "auto",
  overflowX: "hidden",
  /** Avoid width reflow when vertical scrollbar appears after refetch (e.g. severity filter). */
  scrollbarGutter: "stable",
};

/** Same scroll area height for list table and grid (keeps both views aligned). */
const HISTORY_LIST_VIEW_HEIGHT = "calc(100vh - 345px)";

/** Must match `AlertHistorySettings.jsx` — single source for footer + settings dialog. */
const ALERT_HISTORY_SETTINGS_STORAGE_KEY = "alertHistorySettings";
const ALERT_HISTORY_SETTINGS_DEFAULTS = {
  defaultPagination: 10,
  defaultView: "grid",
  /** Match GET /alerts/history default (sort_by=created_at, sort_order=desc). */
  defaultSorting: "timestamp",
};

function readAlertHistorySettingsFromStorage() {
  try {
    const raw = localStorage.getItem(ALERT_HISTORY_SETTINGS_STORAGE_KEY);
    if (!raw) return { ...ALERT_HISTORY_SETTINGS_DEFAULTS };
    const merged = {
      ...ALERT_HISTORY_SETTINGS_DEFAULTS,
      ...JSON.parse(raw),
    };
    /** Legacy default "By Name" grouped rows and fought GET /alerts/history `created_at` order. */
    if (merged.defaultSorting === "alertName") {
      const next = { ...merged, defaultSorting: "timestamp" };
      localStorage.setItem(
        ALERT_HISTORY_SETTINGS_STORAGE_KEY,
        JSON.stringify(next),
      );
      return next;
    }
    return merged;
  } catch {
    return { ...ALERT_HISTORY_SETTINGS_DEFAULTS };
  }
}

function persistAlertHistoryDefaultPagination(pageSize) {
  const n = Number(pageSize);
  if (Number.isNaN(n) || n <= 0) return;
  const next = {
    ...readAlertHistorySettingsFromStorage(),
    defaultPagination: n,
  };
  localStorage.setItem(
    ALERT_HISTORY_SETTINGS_STORAGE_KEY,
    JSON.stringify(next),
  );
}

/** e.g. "11:10 AM 12feb2026" to match reference layout */
function formatHistoryDateChip(iso) {
  try {
    const d = parseAlertApiDateTime(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const day = d.getDate();
    const mon = d
      .toLocaleString("en-US", { month: "short" })
      .replace(".", "")
      .toLowerCase();
    const year = d.getFullYear();
    return `${time} ${day}${mon}${year}`;
  } catch {
    return "—";
  }
}

function formatSeverityDisplay(raw) {
  if (raw === undefined || raw === null || raw === "") return "—";
  const s = String(raw).replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Tune-menu priority: match display label and raw severity (e.g. `critical` vs `high`). */
function historyAlertMatchesPriorityFilter(alert, filterPriority) {
  if (!filterPriority || filterPriority.length === 0) return true;
  const sl =
    alert.severityLabel != null ? String(alert.severityLabel).trim() : "";
  const fromApiLabel = sl !== "" && sl !== "—" ? sl : "";
  const label =
    fromApiLabel ||
    formatSeverityDisplay(alert.severity) ||
    "";
  if (!label || label === "—") return false;
  const ln = label.toLowerCase();
  const raw = String(alert.severity ?? "")
    .replace(/_/g, " ")
    .trim()
    .toLowerCase();
  return filterPriority.some((p) => {
    const pl = String(p).trim().toLowerCase();
    if (!pl) return false;
    return ln === pl || raw === pl;
  });
}

function formatAlertTypeDisplay(raw) {
  if (!raw) return "—";
  return String(raw)
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Normalize alert type strings so Tune-menu labels match `alertType` and `alertTypeRaw` (slug vs title case). */
function normalizeHistoryAlertTypeToken(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return s === "" || s === "—" ? "" : s;
}

function historyAlertMatchesTuneAlertTypes(alert, menuFilterAlertTypes) {
  if (!menuFilterAlertTypes || menuFilterAlertTypes.length === 0) return true;
  const rowUseCase = String(alert?.useCaseId ?? "").trim();
  const keys = new Set();
  for (const v of [alert?.alertType, alert?.alertTypeRaw]) {
    const n = normalizeHistoryAlertTypeToken(v);
    if (n) keys.add(n);
  }
  const rawCombined = String(alert?.alertTypeRaw ?? alert?.alertType ?? "").trim();
  if (rawCombined) {
    keys.add(normalizeHistoryAlertTypeToken(formatAlertTypeDisplay(rawCombined)));
  }
  return menuFilterAlertTypes.some((t) => {
    const selRaw = String(t ?? "").trim();
    if (!selRaw) return false;
    if (isAlertHistoryUsecaseId(selRaw)) {
      return rowUseCase !== "" && rowUseCase === selRaw;
    }
    const sel = normalizeHistoryAlertTypeToken(selRaw);
    if (!sel) return false;
    return keys.has(sel);
  });
}

/**
 * Detections may be nested `{ id, created_at, detections: [...] }` or a plain array or under event_json.
 * @param {Record<string, unknown>} apiAlert
 * @returns {unknown[]}
 */
function detectionsListFromAlert(apiAlert) {
  const ej = apiAlert.event_json;
  const fromEvent =
    ej && typeof ej === "object" && Array.isArray(ej.detections)
      ? ej.detections
      : [];
  if (fromEvent.length > 0) return fromEvent;
  const block = apiAlert.detections;
  if (block && typeof block === "object" && Array.isArray(block.detections)) {
    return block.detections;
  }
  if (Array.isArray(block)) return block;
  return [];
}

/**
 * Alert lifecycle only (active / acknowledged / closed) — not notification delivery.
 * @see deriveWorkspaceStatus in `alertStreamUtils.js` (same rules).
 */
function deriveWorkspaceStatus(apiAlert, eventJson) {
  const s = (
    apiAlert.status ||
    apiAlert.alert_status ||
    eventJson.status ||
    ""
  )
    .toString()
    .toLowerCase();
  if (s.includes("ack")) return "Acknowledged";
  if (s.includes("close") || s.includes("resolved")) return "Closed";
  return "Active";
}

function getEffectiveWorkspaceStatus(alert, localStatusOverrides) {
  if (!alert) return "Active";
  return (
    localStatusOverrides[alert.id] ??
    alert.workspaceStatus ??
    alert.status ??
    "Active"
  );
}

const HISTORY_STATUS_FILTER_OPTIONS = [
  "Active",
  "Acknowledged",
  "Closed",
];

/** Tune filter: matches API `notification_status` (delivery), not workspace alert status. */
const HISTORY_NOTIFICATION_DELIVERY_FILTER_OPTIONS = [
  // { value: "pending", label: "Pending" },
  { value: "queued", label: "Queued" },
  { value: "sent", label: "Sent" },
  { value: "partial", label: "Partial" },
  { value: "failed", label: "Failed" },
];

function historyNotificationDeliveryRowMatchesFilter(alert, selectedValues) {
  if (!selectedValues || selectedValues.length === 0) return true;
  const raw = String(alert?.notificationStatusRaw ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return false;
  return selectedValues.some((sel) => {
    const s = String(sel).trim().toLowerCase();
    if (!s) return false;
    if (raw === s) return true;
    if (s === "sent")
      return (
        raw === "sent" ||
        raw === "success" ||
        raw === "delivered" ||
        raw === "completed"
      );
    return false;
  });
}

/**
 * Toolbar search across operator fields only (no message body / timestamps).
 * Works on the current page; also used when the query is under 2 chars so we do
 * not call GET /alerts/history `search` at all.
 */
function historyAlertMatchesLocalSearch(alert, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;
  const rawType = alert?.alertTypeRaw;
  const parts = [
    alert?.alertName,
    alert?.alertType,
    rawType != null &&
    String(rawType).trim() !== "" &&
    !isAlertHistoryUsecaseId(rawType)
      ? rawType
      : null,
    alert?.sourceName,
    alert?.zoneName,
    alert?.notifiedTo,
    alert?.severityLabel,
    alert?.severity,
    alert?.status,
    alert?.workspaceStatus,
    alert?.notificationStatusRaw,
    alert?.srId,
  ].map((v) => String(v ?? "").toLowerCase());
  const chans = [];
  if (Array.isArray(alert?.notifyChannels)) {
    for (const c of alert.notifyChannels) chans.push(String(c ?? ""));
  }
  if (Array.isArray(alert?.actionTypes)) {
    for (const c of alert.actionTypes) chans.push(String(c ?? ""));
  }
  if (parts.some((p) => p.includes(q))) return true;
  return chans.some((c) => c.toLowerCase().includes(q));
}

function historyMediaFlags(a) {
  const videoStr = String(a?.videoUrl ?? "").trim();
  const hasVideo =
    Boolean(a?.hasVideo) ||
    (videoStr !== "" &&
      videoStr.toLowerCase() !== "null" &&
      videoStr.toLowerCase() !== "undefined");
  const photoStr =
    String(a?.photoUrl ?? "").trim() || String(a?.thumbnailUrl ?? "").trim();
  const hasPhoto =
    Boolean(a?.hasPhoto) ||
    (photoStr !== "" &&
      photoStr.toLowerCase() !== "null" &&
      photoStr.toLowerCase() !== "undefined");
  return {
    hasVideo,
    hasPhoto,
    hasBoth: hasVideo && hasPhoto,
  };
}

/**
 * ALL: still or clip (or both). PHOTO: still rows without an API-reported clip (`hasVideoFromApi`).
 * VIDEO: broad `hasVideo` (includes canonical `/video` when `detection_id` exists).
 */
function historyAlertMatchesMediaTab(a, mediaType) {
  const { hasVideo, hasPhoto } = historyMediaFlags(a);
  const detId = String(a?.detectionId ?? "").trim();
  if (!hasVideo && !hasPhoto) {
    if (
      detId &&
      (mediaType === "all" || mediaType === "photo") &&
      !Boolean(a?.hasVideoFromApi)
    ) {
      return true;
    }
    return false;
  }
  if (mediaType === "all") return true;
  if (mediaType === "video") return hasVideo;
  if (mediaType === "photo")
    return (hasPhoto || detId !== "") && !Boolean(a?.hasVideoFromApi);
  return true;
}

/** Detail carousel: under PHOTO show only stills; under VIDEO only clips; under ALL show everything. */
function filterHistoryDetailSlidesForMediaTab(slides, mediaTab) {
  if (!Array.isArray(slides) || slides.length === 0) return slides;
  if (mediaTab === "photo")
    return slides.filter((s) => s && s.kind === "image");
  if (mediaTab === "video")
    return slides.filter((s) => s && s.kind === "video");
  return slides;
}

/**
 * List/grid still: `photo_url`, `/detections/{id}/photo`, `thumbnail_url`, then `/detections/{id}/thumbnail`.
 */
function historyListThumbHref(alert) {
  const detId =
    alert?.detectionId != null ? String(alert.detectionId).trim() : "";
  let primary =
    (alert?.photoUrl && String(alert.photoUrl).trim()) || "";
  if (!primary && detId) {
    const rel = detectionMediaPath(detId, "photo");
    primary = rel ? resolveServiceworkerMediaPath(rel) || "" : "";
  }
  const thumbField =
    (alert?.thumbnailUrl && String(alert.thumbnailUrl).trim()) || "";
  if (primary) return primary;
  if (thumbField) return thumbField;
  if (detId) {
    const rel = detectionMediaPath(detId, "thumbnail");
    const u = rel ? resolveServiceworkerMediaPath(rel) || "" : "";
    if (u) return u;
  }
  return "";
}

/**
 * Grid card hero: PHOTO = image only. VIDEO = still/thumbnail as hero when available (full clip in dialog); else inline video. ALL = still first, else clip.
 */
function historyGridHeroMode(alert, mediaTab = "all") {
  const detId =
    alert?.detectionId != null ? String(alert.detectionId).trim() : "";

  let videoHref =
    alert?.videoUrl && String(alert.videoUrl).trim()
      ? String(alert.videoUrl).trim()
      : "";
  if (!videoHref && detId && alert?.hasVideo) {
    const rel = detectionMediaPath(detId, "video");
    videoHref = rel ? resolveServiceworkerMediaPath(rel) || "" : "";
  }

  const photoHref = historyListThumbHref(alert);

  if (mediaTab === "photo") {
    return {
      kind: "image",
      videoHref: "",
      photoHref: photoHref || "",
    };
  }

  if (mediaTab === "video") {
    if (photoHref) {
      return { kind: "image", videoHref: videoHref || "", photoHref };
    }
    if (videoHref) {
      return { kind: "video", videoHref, photoHref: "" };
    }
    return { kind: "image", videoHref: "", photoHref: "" };
  }

  if (photoHref) {
    return { kind: "image", videoHref, photoHref };
  }
  if (videoHref) {
    return { kind: "video", videoHref, photoHref };
  }
  return { kind: "image", videoHref: "", photoHref: "" };
}

/** List source column preview: PHOTO = still only; VIDEO = still/thumbnail when available else clip; ALL = still then clip. */
function historyListRowPreview(alert, mediaTab) {
  const hero = historyGridHeroMode(alert, mediaTab);
  if (mediaTab === "video") {
    const thumb = historyListThumbHref(alert);
    if (thumb) return { mode: "image", href: thumb };
    if (hero.kind === "video" && hero.videoHref) {
      return { mode: "video", href: hero.videoHref };
    }
    return { mode: "empty", href: "" };
  }
  if (mediaTab === "photo") {
    const href = historyListThumbHref(alert);
    return href ? { mode: "image", href } : { mode: "empty", href: "" };
  }
  const thumb = historyListThumbHref(alert);
  if (thumb) return { mode: "image", href: thumb };
  if (hero.kind === "video" && hero.videoHref) {
    return { mode: "video", href: hero.videoHref };
  }
  return { mode: "empty", href: "" };
}

/**
 * Detection crop (GET `/detections/{id}/thumbnail`) in the detail dialog — ANPR & Face Recognition only.
 */
function historyAlertTypeUsesDetectionCropThumbnail(alert) {
  if (!alert || typeof alert !== "object") return false;
  const combined = `${alert.alertTypeRaw ?? ""} ${alert.alertType ?? ""}`
    .toLowerCase()
    .replace(/_/g, " ");
  if (!combined.trim()) return false;

  const isAnpr =
    /\banpr\b/.test(combined) ||
    combined.includes("automatic number plate") ||
    combined.includes("number plate recognition");

  const isFaceRecognition =
    combined.includes("face recognition") ||
    combined.includes("face recognization") ||
    combined.includes("facial recognition");

  return Boolean(isAnpr || isFaceRecognition);
}

function formatLocalYmd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function compareHistoryAlerts(a, b, orderBy, order) {
  let cmp = 0;
  if (orderBy === "timestamp") {
    const ta = parseAlertApiDateTime(a.timestamp).getTime();
    const tb = parseAlertApiDateTime(b.timestamp).getTime();
    const na = Number.isNaN(ta) ? 0 : ta;
    const nb = Number.isNaN(tb) ? 0 : tb;
    cmp = na === nb ? 0 : na < nb ? -1 : 1;
  } else {
    const va = a[orderBy];
    const vb = b[orderBy];
    const sa = va == null ? "" : String(va);
    const sb = vb == null ? "" : String(vb);
    cmp = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  }
  return order === "asc" ? cmp : -cmp;
}

// Date range options
const DATE_RANGES = [
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "thisweek", label: "This Week" },
  { value: "thismonth", label: "This Month" },
  { value: "custom", label: "Custom Range..." },
];

// Generate mock alerts with realistic synchronized data
const generateMockAlerts = (baseAlerts) => {
  const mockAlerts = [];
  const alertTypes = [];
  const sources = [];
  const statuses = ["Active", "Resolved", "Acknowledged"];

  for (let i = 0; i < 20; i++) {
    const now = new Date();
    const randomHours = Math.floor(Math.random() * 48);
    const timestamp = new Date(now.getTime() - randomHours * 60 * 60 * 1000);

    const alertType = alertTypes[i % alertTypes.length];
    const sourceName = sources[i % sources.length];
    const status = statuses[i % statuses.length];

    const hasVideo =
      (alertType === "Intrusion" || alertType === "Motion Detection") &&
      i % 3 === 0;
    const hasPhoto = true;

    const actionTypes =
      status === "Active"
        ? ["Email", "SMS"]
        : status === "Acknowledged"
        ? ["Email", "SMS", "Push"]
        : ["Email", "SMS", "Push", "Webhook"];

    mockAlerts.push({
      id: `ALT${String(1000 + i).padStart(4, "0")}`,
      alertName: `${alertType} - ${sourceName}`,
      alertType: alertType,
      sourceName: sourceName,
      timestamp: timestamp.toISOString(),
      actionTypes: actionTypes,
      status: status,
      hasVideo: hasVideo,
      hasPhoto: hasPhoto,
      photoUrl: `https://picsum.photos/seed/${sourceName}-${Math.floor(
        i / 4
      )}/300/200`,
      thumbnailUrl: `https://picsum.photos/seed/${sourceName}-${Math.floor(
        i / 4
      )}/300/200`,
      thumbnailCropUrl: "",
    });
  }

  return [...baseAlerts, ...mockAlerts];
};

function extractCamerasArray(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

function extractZonesArray(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

export function HistoryEnhanced() {
  const { alerts: contextAlerts, alertRules } = useAlertData();

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [dateRange, setDateRange] = useState("all");
  const [imageErrors, setImageErrors] = useState({}); // Track which images failed to load
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [customDateDialog, setCustomDateDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [viewingMonth, setViewingMonth] = useState(new Date());
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [historyThumbLightboxOpen, setHistoryThumbLightboxOpen] =
    useState(false);
  const [historyDetailCarouselIndex, setHistoryDetailCarouselIndex] =
    useState(0);
  const historyDetailMediaAreaRef = useRef(null);
  /** Photo / Video / All: server `media_type` when not ALL, plus client refine (e.g. PHOTO = still-only). */
  const [mediaType, setMediaType] = useState("all");
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  /** Tune menu: extra filters (apply for both video and photo). */
  const [menuFilterAlertTypes, setMenuFilterAlertTypes] = useState([]);
  const [menuFilterStatuses, setMenuFilterStatuses] = useState([]);
  const [isHistoryStatusAllChecked, setIsHistoryStatusAllChecked] =
    useState(true);
  const [menuFilterNotificationDelivery, setMenuFilterNotificationDelivery] =
    useState([]);
  const [isHistoryNotificationStatusAllChecked, setIsHistoryNotificationStatusAllChecked] =
    useState(true);
  const [menuFilterTuneZones, setMenuFilterTuneZones] = useState([]);
  const [isHistorySourceAllChecked, setIsHistorySourceAllChecked] =
    useState(true);
  const [isHistoryZoneAllChecked, setIsHistoryZoneAllChecked] = useState(true);
  const [isHistoryAlertTypeAllChecked, setIsHistoryAlertTypeAllChecked] =
    useState(true);
  // Dropdown states
  const [dateRangeMenuAnchor, setDateRangeMenuAnchor] = useState(null);
  const [sourceNameMenuAnchor, setSourceNameMenuAnchor] = useState(null);
  const [zoneNameMenuAnchor, setZoneNameMenuAnchor] = useState(null);
  const [alertTypeToolbarMenuAnchor, setAlertTypeToolbarMenuAnchor] =
    useState(null);
  const [selectedSourceNames, setSelectedSourceNames] = useState([]);
  const [sourceNameSearch, setSourceNameSearch] = useState("");

  // Filter and Sorting states
  const [filterPriority, setFilterPriority] = useState([]);
  const [isHistoryPriorityAllChecked, setIsHistoryPriorityAllChecked] =
    useState(true);
  const [orderBy, setOrderBy] = useState("timestamp"); // Match API created_at ordering
  const [order, setOrder] = useState("desc");
  const [hoveredRow, setHoveredRow] = useState(null);

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // API data state
  const [apiAlerts, setApiAlerts] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [hasApiResponse, setHasApiResponse] = useState(false); // Track if API has responded
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });

  /** Learn source display name → id from any fetch so Source filter survives Video ↔ Photo. */
  const sourceNameToSourceIdRef = useRef(new Map());
  /** Ignore stale history responses when filters/page change triggers overlapping fetches. */
  const alertHistoryFetchGenerationRef = useRef(0);

  const [historyRowMenuAnchor, setHistoryRowMenuAnchor] = useState(null);
  /** Viewport position for right-click row menu (shared Menu with ⋮). */
  const [historyRowMenuPointerPosition, setHistoryRowMenuPointerPosition] =
    useState(null);
  const [historyRowMenuAlert, setHistoryRowMenuAlert] = useState(null);
  const [localStatusOverrides, setLocalStatusOverrides] = useState({});

  /** Use cases from GET `/use-cases/` — same source as Dashboard Alert Type filter. */
  const [useCasesList, setUseCasesList] = useState([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [useCasesError, setUseCasesError] = useState(null);
  /** Source names for toolbar from GET `/cameras`. */
  const [historyCameras, setHistoryCameras] = useState([]);
  const [historyCamerasLoading, setHistoryCamerasLoading] = useState(false);
  const [historyCamerasError, setHistoryCamerasError] = useState(null);
  /** Zones for toolbar from GET `/zones`. */
  const [historyZones, setHistoryZones] = useState([]);
  const [historyZonesLoading, setHistoryZonesLoading] = useState(false);
  const [historyZonesError, setHistoryZonesError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const extractUseCases = (body) => {
      if (Array.isArray(body)) return body;
      if (body && typeof body === "object") {
        const arr = body.data ?? body.results ?? body.Data;
        if (Array.isArray(arr)) return arr;
      }
      return [];
    };

    const loadUseCases = async () => {
      setUseCasesLoading(true);
      setUseCasesError(null);
      try {
        const body = await useCasesAPI.list();
        if (cancelled) return;
        setUseCasesList(extractUseCases(body));
      } catch (e) {
        if (!cancelled) {
          setUseCasesError(e instanceof Error ? e : new Error(String(e)));
          setUseCasesList([]);
        }
      } finally {
        if (!cancelled) setUseCasesLoading(false);
      }
    };

    void loadUseCases();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadZones = async () => {
      setHistoryZonesLoading(true);
      setHistoryZonesError(null);
      try {
        const body = await zonesAPI.list();
        if (cancelled) return;
        const raw = extractZonesArray(body);
        const list = raw
          .filter((z) => z && typeof z === "object")
          .map((z) => ({
            id: String(z.id ?? "").trim(),
            cameraId: String(z.camera_id ?? z.cameraId ?? "").trim(),
            name: String(z.name ?? "").trim(),
          }))
          .filter((z) => z.id && z.name);
        setHistoryZones(list);
      } catch (e) {
        if (cancelled) return;
        setHistoryZonesError(e instanceof Error ? e : new Error(String(e)));
        setHistoryZones([]);
      } finally {
        if (!cancelled) setHistoryZonesLoading(false);
      }
    };

    void loadZones();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCameras = async () => {
      setHistoryCamerasLoading(true);
      setHistoryCamerasError(null);
      try {
        const body = await camerasAPI.list();
        if (cancelled) return;
        const raw = extractCamerasArray(body);
        const list = raw
          .filter((c) => c && typeof c === "object")
          .map((c) => {
            const id = String(c.id ?? c.camera_id ?? "").trim();
            const name = String(c.name ?? "").trim();
            return { id, name };
          })
          .filter((c) => c.name.length > 0);
        setHistoryCameras(list);
      } catch (e) {
        if (cancelled) return;
        setHistoryCamerasError(e instanceof Error ? e : new Error(String(e)));
        setHistoryCameras([]);
      } finally {
        if (!cancelled) setHistoryCamerasLoading(false);
      }
    };

    void loadCameras();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load settings from localStorage on mount
  useEffect(() => {
    const parsed = readAlertHistorySettingsFromStorage();
    if (parsed.defaultPagination != null) {
      const n = Number(parsed.defaultPagination);
      if (!Number.isNaN(n) && n > 0) setItemsPerPage(n);
    }
    if (parsed.defaultView) {
      setViewMode(parsed.defaultView);
    }
    if (parsed.defaultSorting) {
      setOrderBy(parsed.defaultSorting);
      if (parsed.defaultSorting === "alertName") {
        setOrder("asc");
      } else if (parsed.defaultSorting === "timestamp") {
        setOrder("desc");
      } else {
        setOrder("asc");
      }
    }
  }, []);

  // Handle settings save
  const handleSettingsSave = (settings) => {
    // Apply settings immediately
    if (settings.defaultPagination != null) {
      const n = Number(settings.defaultPagination);
      if (!Number.isNaN(n) && n > 0) setItemsPerPage(n);
    }
    if (settings.defaultView) {
      setViewMode(settings.defaultView);
    }
    if (settings.defaultSorting) {
      setOrderBy(settings.defaultSorting);
      // Set order to "asc" for name sorting, "desc" for timestamp
      if (settings.defaultSorting === "alertName") {
        setOrder("asc");
      } else if (settings.defaultSorting === "timestamp") {
        setOrder("desc");
      } else {
        setOrder("asc");
      }
    }
  };

  // Transform API response to match component's expected format
  const transformApiAlert = (apiAlert) => {
    const eventJson =
      apiAlert.event_json && typeof apiAlert.event_json === "object"
        ? apiAlert.event_json
        : {};
    const detBlock = apiAlert.detections;
    const detCreatedAt =
      detBlock && typeof detBlock === "object" && detBlock.created_at != null
        ? detBlock.created_at
        : null;

    const detectionsArr = detectionsListFromAlert(apiAlert);

    const media = getMediaFieldsFromAlertApiPayload(apiAlert);
    const {
      detectionId,
      photoUrl,
      thumbnailUrl,
      thumbnailCropUrl,
      videoUrl,
      hasVideo,
      hasVideoFromApi,
    } = media;

    const detectionLabel =
      detectionsArr.length > 0 &&
      detectionsArr[0] &&
      typeof detectionsArr[0] === "object" &&
      detectionsArr[0].label != null
        ? String(detectionsArr[0].label)
        : null;

    const alertName =
      apiAlert.alert_name || apiAlert.message || "Unknown Alert";

    const resolvedTypeName = resolveAlertTypeNameRaw(apiAlert);
    const hasTypeName = Boolean(resolvedTypeName);
    const alertTypeRaw = hasTypeName
      ? resolvedTypeName
      : apiAlert.alert_type ||
        eventJson.alert_type ||
        (detectionLabel
          ? `${detectionLabel.charAt(0).toUpperCase() + detectionLabel.slice(1)} Detection`
          : null) ||
        (apiAlert.message && apiAlert.message.includes("Security")
          ? "Security Alert"
          : null) ||
        "Unknown";
    const alertTypeDisplay = hasTypeName
      ? alertTypeRaw
      : formatAlertTypeDisplay(alertTypeRaw);

    const sourceName =
      apiAlert.source_name ||
      apiAlert.sourceName ||
      "Unknown source";

    /** Same field order as Dashboard `mapAlertResponseToDashboardRow` + UTC-naive parse (history REST). */
    const rawTs =
      eventJson.created_at ||
      detCreatedAt ||
      apiAlert.created_at ||
      apiAlert.updated_at ||
      apiAlert.timestamp ||
      new Date().toISOString();
    const parsedTs = parseAlertApiDateTime(rawTs);
    const timestamp = Number.isNaN(parsedTs.getTime())
      ? new Date().toISOString()
      : parsedTs.toISOString();

    const rawPhotoUrl =
      typeof apiAlert.photo_url === "string" ? apiAlert.photo_url.trim() : "";
    const rawThumbUrl =
      typeof apiAlert.thumbnail_url === "string"
        ? apiAlert.thumbnail_url.trim()
        : "";
    const hasRawImagePath =
      (rawPhotoUrl && rawPhotoUrl.toLowerCase() !== "null") ||
      (rawThumbUrl && rawThumbUrl.toLowerCase() !== "null");
    const hasPhoto =
      apiAlert.hasPhoto ||
      apiAlert.media_type === "photo" ||
      Boolean(photoUrl || thumbnailUrl) ||
      hasRawImagePath;

    const notifiedToDisplay = formatAlertNotifiedToDisplay(apiAlert);

    const zoneName =
      apiAlert.zone_name ||
      apiAlert.zoneName ||
      (eventJson.zone != null && String(eventJson.zone).trim() !== ""
        ? String(eventJson.zone)
        : "—");

    const rowId =
      apiAlert.id != null
        ? String(apiAlert.id)
        : apiAlert._id != null
          ? String(apiAlert._id)
          : `row-${timestamp}-${Math.random().toString(36).slice(2, 11)}`;

    const workspaceStatus = deriveWorkspaceStatus(apiAlert, eventJson);
    const channels = notifyChannelsFromAlert(apiAlert);
    const legacyTypes = apiAlert.action_types || apiAlert.actionTypes || [];
    const useCaseId = resolveDashboardRowUseCaseId(
      apiAlert,
      eventJson,
      alertRules,
    );

    /** Prefer rule severity for display/filter (matches rule configuration). */
    const sev =
      apiAlert.rule_severity ??
      apiAlert.severity ??
      apiAlert.use_case_severity;
    const sevLabelRaw =
      (typeof apiAlert.rule_severity_label === "string" &&
        apiAlert.rule_severity_label.trim()) ||
      (typeof apiAlert.ruleSeverityLabel === "string" &&
        apiAlert.ruleSeverityLabel.trim()) ||
      (typeof apiAlert.severity_label === "string" &&
        apiAlert.severity_label.trim()) ||
      (typeof apiAlert.severityLabel === "string" &&
        apiAlert.severityLabel.trim()) ||
      "";
    const severityLabel = sevLabelRaw
      ? formatSeverityDisplay(sevLabelRaw.replace(/_/g, " "))
      : formatSeverityDisplay(sev);

    const eventJsonForSr =
      apiAlert.event_json && typeof apiAlert.event_json === "object"
        ? apiAlert.event_json
        : null;
    const srId =
      readSrIdFromUnknown(apiAlert) ?? readSrIdFromUnknown(eventJsonForSr);

    return {
      id: rowId,
      srId,
      alertName,
      alertType: alertTypeDisplay,
      alertTypeRaw,
      useCaseId,
      sourceName,
      timestamp,
      status: workspaceStatus,
      workspaceStatus,
      notificationStatusRaw:
        apiAlert.notification_status != null
          ? String(apiAlert.notification_status).trim().toLowerCase()
          : "",
      detectionId,
      photoUrl: photoUrl || "",
      thumbnailUrl: thumbnailUrl || "",
      /** GET /detections/{id}/thumbnail — detail dialog bottom-right crop (ANPR / face). */
      thumbnailCropUrl: thumbnailCropUrl || "",
      videoUrl: videoUrl || undefined,
      actionTypes: channels.length > 0 ? channels : legacyTypes,
      notifyChannels: channels,
      notifyChannelItems: buildNotifyChannelItems(apiAlert),
      hasVideo: hasVideo,
      /** True only when API / explicit `video_url` indicates a clip (not synthetic `/detections/.../video` alone). */
      hasVideoFromApi: Boolean(hasVideoFromApi),
      hasPhoto: hasPhoto,
      ruleId: apiAlert.rule_id,
      sourceId: apiAlert.source_id,
      roiId: apiAlert.roi_id,
      severity: sev,
      severityLabel,
      zoneName,
      notifiedTo: notifiedToDisplay,
      detections: detectionsArr,
      message: apiAlert.message,
    };
  };

  useEffect(() => {
    if (!detailDialogOpen) setHistoryThumbLightboxOpen(false);
  }, [detailDialogOpen]);

  useEffect(() => {
    if (detailDialogOpen) setHistoryDetailCarouselIndex(0);
  }, [detailDialogOpen, selectedAlert?.id, mediaType]);

  useEffect(() => {
    if (!historyThumbLightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setHistoryThumbLightboxOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [historyThumbLightboxOpen]);

  /** Latest fetch impl for Alert History (ref avoids stale closures on manual refresh). */
  const fetchAlertHistoryRef = useRef(async () => {});

  fetchAlertHistoryRef.current = async () => {
    const fetchGen = ++alertHistoryFetchGenerationRef.current;
    setLoadingAlerts(true);
    // Do not clear `hasApiResponse` here: refetches would empty `allAlerts` while the
    // Tune menu is open and status/… toggles trigger this path — shrinking then re-growing the menu.
    try {
      const pageSize = Math.min(
        200,
        Math.max(1, Number(itemsPerPage) || 10),
      );
      const params = {
        page_size: pageSize,
        page: currentPage,
      };

      // API: `search` has minLength 2; shorter queries are handled client-side only.
      const trimmedSearch = searchQuery && String(searchQuery).trim();
      if (trimmedSearch && trimmedSearch.length >= 2) {
        params.search = trimmedSearch;
      }

      // Add period filter based on date range
      const getPeriodForAPI = (range) => {
        const periodMap = {
          today: "today",
          yesterday: "yesterday",
          last7days: "last_7_days",
          last30days: "last_30_days",
          thisweek: "this_week",
          thismonth: "this_month",
          custom: "custom", // Will need custom date handling
        };
        return periodMap[range] || "last_7_days";
      };

      if (dateRange === "all") {
        // Omit period — backend returns full history when not constrained
      } else if (dateRange !== "custom") {
        params.period = getPeriodForAPI(dateRange);
      } else if (dateRange === "custom" && startDate && endDate) {
        // Local calendar bounds; end uses same calendar day through 23:59:59 so single-day
        // ranges are not zero-width (start/end both at midnight would miss all rows).
        params.start_date = `${formatLocalYmd(startDate)}T00:00:00`;
        params.end_date = `${formatLocalYmd(endDate)}T23:59:59`;
      }

      if (mediaType === "photo" || mediaType === "video") {
        params.media_type = mediaType;
      }

      // Source Name filter: API now accepts comma-separated `source_name` values.
      if (!isHistorySourceAllChecked && selectedSourceNames.length > 0) {
        const sourceNames = [
          ...new Set(
            selectedSourceNames
              .map((n) => String(n).trim())
              .filter(Boolean),
          ),
        ];
        if (sourceNames.length > 0) {
          params.source_name = sourceNames.join(",");
        }
      }

      // Zone filter: backend accepts comma-separated `roi_id` values.
      if (!isHistoryZoneAllChecked && menuFilterTuneZones.length > 0) {
        const roiIds = [
          ...new Set(
            menuFilterTuneZones.map((id) => String(id).trim()).filter(Boolean),
          ),
        ];
        if (roiIds.length > 0) params.roi_id = roiIds.join(",");
      }

      if (!isHistoryAlertTypeAllChecked) {
        const alertTypeIds = menuFilterAlertTypes
          .map((t) => String(t).trim())
          .filter((t) => isAlertHistoryUsecaseId(t));
        if (alertTypeIds.length > 0) {
          params.alert_type = alertTypeIds.join(",");
        }
      }

      if (!isHistoryStatusAllChecked && menuFilterStatuses.length > 0) {
        const statusMap = {
          Active: "active",
          Acknowledged: "acknowledged",
          Closed: "closed",
        };
        const vals = [
          ...new Set(
            menuFilterStatuses.map(
              (s) => statusMap[s] || String(s).toLowerCase(),
            ),
          ),
        ];
        if (vals.length > 0) {
          params.status = vals.join(",");
        }
      }

      if (
        !isHistoryNotificationStatusAllChecked &&
        menuFilterNotificationDelivery.length > 0
      ) {
        const nd = [
          ...new Set(
            menuFilterNotificationDelivery
              .map((v) => String(v).trim().toLowerCase())
              .filter(Boolean),
          ),
        ];
        if (nd.length > 0) {
          params.notification_status = nd.join(",");
        }
      }

      if (!isHistoryPriorityAllChecked && filterPriority.length > 0) {
        const sevVals = [
          ...new Set(
            filterPriority
              .map((p) => String(p).trim().toLowerCase())
              .filter(Boolean),
          ),
        ];
        if (sevVals.length > 0) {
          params.severity = sevVals.join(",");
        }
      }

      if (orderBy === "timestamp") {
        params.sort_by = "created_at";
        params.sort_order = order === "asc" ? "asc" : "desc";
      }

      // Make single API call with all filters (including comma-separated values)
      console.log("API call with filters:", params);
      let response;
      try {
        response = await alertsHistoryAPI.getHistory(params);
      } catch (firstErr) {
        if (params.search != null) {
          const retryParams = { ...params };
          delete retryParams.search;
          console.warn(
            "Alert history search request failed; retrying without server search:",
            firstErr,
          );
          response = await alertsHistoryAPI.getHistory(retryParams);
        } else {
          throw firstErr;
        }
      }
      if (fetchGen !== alertHistoryFetchGenerationRef.current) return;
      console.log("API Response:", response);

      // Extract alerts array from response
      // API response structure: { success: true, message: "...", data: { alerts: [...], total, page, per_page, total_pages } }
      let alertsArray = [];
      const responseData = response.data || response;

      // Check for nested data structure first (response.data.alerts)
      if (
        responseData &&
        responseData.alerts &&
        Array.isArray(responseData.alerts)
      ) {
        alertsArray = responseData.alerts;
      } else if (
        response &&
        response.alerts &&
        Array.isArray(response.alerts)
      ) {
        alertsArray = response.alerts;
      } else if (response && Array.isArray(response)) {
        alertsArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        alertsArray = response.data;
      } else if (
        response &&
        response.results &&
        Array.isArray(response.results)
      ) {
        alertsArray = response.results;
      }

      console.log("Extracted alerts array:", alertsArray);

      if (fetchGen !== alertHistoryFetchGenerationRef.current) return;

      // Store pagination metadata from API response
      // Check both response.data and response for pagination info
      const paginationData = responseData || response;
      let perPageResolved = itemsPerPage;
      if (
        paginationData &&
        (paginationData.total !== undefined ||
          paginationData.total_pages !== undefined)
      ) {
        perPageResolved =
          paginationData.page_size ??
          paginationData.per_page ??
          itemsPerPage;
        setPaginationMeta({
          total: paginationData.total || alertsArray.length,
          totalPages:
            paginationData.total_pages ||
            Math.ceil(
              (paginationData.total || alertsArray.length) / perPageResolved
            ),
          currentPage: paginationData.page || currentPage,
          perPage: perPageResolved,
        });
      } else {
        // Fallback: calculate from array length
        setPaginationMeta({
          total: alertsArray.length,
          totalPages: Math.ceil(alertsArray.length / itemsPerPage),
          currentPage: currentPage,
          perPage: itemsPerPage,
        });
      }

      // Transform each alert to match expected format
      // If no alerts in response, set to empty array and mark API as responded
      if (alertsArray.length === 0) {
        console.log(
          "No alerts found in API response, setting to empty array"
        );
        if (fetchGen !== alertHistoryFetchGenerationRef.current) return;
        setHasApiResponse(true);
        setApiAlerts([]);
      } else {
        const transformedAlerts = alertsArray.map(transformApiAlert);
        console.log("Transformed Alerts:", transformedAlerts); // Debug log
        if (fetchGen !== alertHistoryFetchGenerationRef.current) return;
        setHasApiResponse(true);
        setApiAlerts(transformedAlerts);
      }
    } catch (error) {
      console.error("Error fetching alert history:", error);
      if (fetchGen === alertHistoryFetchGenerationRef.current) {
        setHasApiResponse(false);
        setApiAlerts(null);
      }
    } finally {
      if (fetchGen === alertHistoryFetchGenerationRef.current) {
        setLoadingAlerts(false);
      }
    }
  };

  const handleRefreshHistoryClick = useCallback(() => {
    void fetchAlertHistoryRef.current?.();
  }, []);

  // Fetch alerts from API (debounced search; filter changes re-run)
  useEffect(() => {
    const run = () => {
      void fetchAlertHistoryRef.current?.();
    };
    // If search query exists, debounce API calls - wait 500ms after user stops typing
    // Otherwise, fetch immediately on mount or when filters change
    if (searchQuery && searchQuery.trim()) {
      const timeoutId = setTimeout(run, 500);
      return () => clearTimeout(timeoutId);
    }
    run();
  }, [
    searchQuery,
    dateRange,
    startDate,
    endDate,
    mediaType,
    itemsPerPage,
    currentPage,
    isHistorySourceAllChecked,
    selectedSourceNames,
    isHistoryStatusAllChecked,
    menuFilterStatuses,
    isHistoryAlertTypeAllChecked,
    menuFilterAlertTypes,
    isHistoryZoneAllChecked,
    menuFilterTuneZones,
    isHistoryNotificationStatusAllChecked,
    menuFilterNotificationDelivery,
    isHistoryPriorityAllChecked,
    filterPriority,
    orderBy,
    order,
  ]);

  useEffect(() => {
    for (const c of historyCameras) {
      const sn = c?.name != null ? String(c.name).trim() : "";
      const sid = c?.id != null ? String(c.id).trim() : "";
      if (sn && sid) {
        sourceNameToSourceIdRef.current.set(sn, sid);
      }
    }
  }, [historyCameras]);

  useEffect(() => {
    if (!apiAlerts || apiAlerts.length === 0) return;
    for (const a of apiAlerts) {
      const sn = a.sourceName != null ? String(a.sourceName).trim() : "";
      if (sn && a.sourceId != null && a.sourceId !== "") {
        sourceNameToSourceIdRef.current.set(sn, a.sourceId);
      }
    }
  }, [apiAlerts]);

  const allAlerts = useMemo(() => {
    // If API has responded (hasApiResponse is true), use apiAlerts (even if empty array)
    // If API hasn't responded (hasApiResponse is false), return empty array to display nothing
    if (hasApiResponse && apiAlerts !== null) {
      return apiAlerts;
    }
    // When API gives no response, return empty array to display nothing
    return [];
  }, [apiAlerts, hasApiResponse]);

  const uniqueSourceNames = useMemo(() => {
    const set = new Set();
    for (const c of historyCameras) {
      const name = c?.name != null ? String(c.name).trim() : "";
      if (name) set.add(name);
    }
    for (const a of allAlerts) {
      const name = a?.sourceName != null ? String(a.sourceName).trim() : "";
      if (name) set.add(name);
    }
    for (const sel of selectedSourceNames) {
      const name = String(sel ?? "").trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [historyCameras, allAlerts, selectedSourceNames]);

  const filteredSourceNames = uniqueSourceNames.filter((name) =>
    name.toLowerCase().includes(sourceNameSearch.toLowerCase())
  );

  const alertTypeTuneMenuOptions = useMemo(() => {
    const out = [];
    const seenValues = new Set();
    const seenNorm = new Set();
    const source = Array.isArray(useCasesList) ? useCasesList : [];
    for (const uc of source) {
      const id = uc?.id != null ? String(uc.id).trim() : "";
      const name = uc?.name != null ? String(uc.name).trim() : "";
      if (!id || !name) continue;
      if (!isAlertHistoryUsecaseId(id)) continue;
      if (seenValues.has(id)) continue;
      seenValues.add(id);
      const norm = normalizeHistoryAlertTypeToken(name);
      if (norm) seenNorm.add(norm);
      out.push({ value: id, label: name });
    }
    for (const a of allAlerts) {
      const id = String(a?.useCaseId ?? "").trim();
      if (id && isAlertHistoryUsecaseId(id) && !seenValues.has(id)) {
        seenValues.add(id);
        const label = String(a.alertType ?? "").trim() || id;
        const n = normalizeHistoryAlertTypeToken(label);
        if (n) seenNorm.add(n);
        out.push({ value: id, label });
        continue;
      }
      const raw =
        a?.alertTypeRaw != null && String(a.alertTypeRaw).trim() !== ""
          ? String(a.alertTypeRaw).trim()
          : String(a?.alertType ?? "").trim();
      if (!raw || raw === "—" || raw === "Unknown") continue;
      const norm = normalizeHistoryAlertTypeToken(raw);
      if (!norm || seenValues.has(raw) || seenNorm.has(norm)) continue;
      seenValues.add(raw);
      seenNorm.add(norm);
      out.push({
        value: raw,
        label:
          String(a.alertType ?? formatAlertTypeDisplay(raw)).trim() || raw,
      });
    }
    out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return out;
  }, [useCasesList, allAlerts]);

  const zoneToolbarOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    const cameraById = new Map();
    for (const c of historyCameras) {
      const id = String(c?.id ?? "").trim();
      const name = String(c?.name ?? "").trim();
      if (id && name && !cameraById.has(id)) cameraById.set(id, name);
    }
    for (const z of historyZones) {
      const id = String(z?.id ?? "").trim();
      const zoneName = String(z?.name ?? "").trim();
      const cameraId = String(z?.cameraId ?? "").trim();
      if (!id || !zoneName || seen.has(id)) continue;
      seen.add(id);
      const cameraName = cameraId ? cameraById.get(cameraId) || "" : "";
      const label = cameraName ? `${cameraName} ${zoneName}` : zoneName;
      out.push({ value: id, label, zoneName });
    }
    for (const a of allAlerts) {
      const id = String(a?.roiId ?? "").trim();
      const zoneName =
        a?.zoneName != null && String(a.zoneName).trim() !== ""
          ? String(a.zoneName).trim()
          : "—";
      if (!id || !zoneName || seen.has(id)) continue;
      seen.add(id);
      out.push({ value: id, label: zoneName, zoneName });
    }
    out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return out;
  }, [historyCameras, historyZones, allAlerts]);

  const selectedHistoryZoneNames = useMemo(() => {
    const out = new Set();
    for (const zid of menuFilterTuneZones) {
      const id = String(zid ?? "").trim();
      if (!id) continue;
      const hit = zoneToolbarOptions.find((z) => z.value === id);
      if (hit?.zoneName) out.add(hit.zoneName);
    }
    return out;
  }, [menuFilterTuneZones, zoneToolbarOptions]);

  const menuFilterAlertTypeLabels = useMemo(
    () =>
      menuFilterAlertTypes.map((id) => {
        const o = alertTypeTuneMenuOptions.find((x) => x.value === id);
        return o?.label ?? String(id);
      }),
    [menuFilterAlertTypes, alertTypeTuneMenuOptions],
  );

  const getSourceNameDisplay = () => {
    if (isHistorySourceAllChecked) return "Sub-sources";
    if (selectedSourceNames.length === 0) return "No sub-sources";
    return selectedSourceNames.length === 1
      ? selectedSourceNames[0]
      : `${selectedSourceNames.length} selected`;
  };

  const getZoneToolbarDisplay = () => {
    if (isHistoryZoneAllChecked) return "Zones";
    if (menuFilterTuneZones.length === 0) return "No zones";
    if (menuFilterTuneZones.length === 1) {
      const hit = zoneToolbarOptions.find(
        (z) => z.value === menuFilterTuneZones[0],
      );
      return hit?.label ?? String(menuFilterTuneZones[0]);
    }
    return `${menuFilterTuneZones.length} selected`;
  };

  const getAlertTypeToolbarDisplay = () => {
    if (isHistoryAlertTypeAllChecked) return "Alert types";
    if (menuFilterAlertTypes.length === 0) return "No alert types";
    if (menuFilterAlertTypes.length === 1) {
      const hit = alertTypeTuneMenuOptions.find(
        (x) => x.value === menuFilterAlertTypes[0],
      );
      return hit?.label ?? String(menuFilterAlertTypes[0]);
    }
    return `${menuFilterAlertTypes.length} selected`;
  };

  const getDateRangeDisplay = () => {
    const rangeMap = {
      all: "All dates",
      today: "Today",
      yesterday: "Yesterday",
      last7days: "Last 7 days",
      last30days: "Last 30 days",
      thisweek: "This week",
      thismonth: "This month",
      custom:
        startDate && endDate
          ? `${startDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })} - ${endDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}`
          : "Custom range",
    };
    return rangeMap[dateRange] || rangeMap.all;
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setDateRangeMenuAnchor(null);
    if (range === "custom") {
      setTempStartDate(startDate || null);
      setTempEndDate(endDate || null);
      setViewingMonth(startDate || new Date());
      setCustomDateDialog(true);
    }
  };

  const handleApplyCustomRange = () => {
    if (tempStartDate && tempEndDate) {
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setCustomDateDialog(false);
    }
  };

  const handleCancelCustomRange = () => {
    setCustomDateDialog(false);
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const filteredAlerts = useMemo(() => {
    let filtered = [...allAlerts];

    if (searchQuery && String(searchQuery).trim()) {
      filtered = filtered.filter((a) =>
        historyAlertMatchesLocalSearch(a, searchQuery),
      );
    }

    if (!isHistorySourceAllChecked && selectedSourceNames.length > 0) {
      filtered = filtered.filter((a) =>
        selectedSourceNames.includes(a.sourceName)
      );
    }

    filtered = filtered.filter((a) => historyAlertMatchesMediaTab(a, mediaType));

    if (!isHistoryPriorityAllChecked && filterPriority.length > 0) {
      filtered = filtered.filter((a) =>
        historyAlertMatchesPriorityFilter(a, filterPriority),
      );
    }

    if (!isHistoryAlertTypeAllChecked && menuFilterAlertTypes.length > 0) {
      filtered = filtered.filter((a) =>
        historyAlertMatchesTuneAlertTypes(a, menuFilterAlertTypes),
      );
    }

    if (!isHistoryStatusAllChecked && menuFilterStatuses.length > 0) {
      filtered = filtered.filter((a) =>
        menuFilterStatuses.includes(
          getEffectiveWorkspaceStatus(a, localStatusOverrides),
        ),
      );
    }

    if (
      !isHistoryNotificationStatusAllChecked &&
      menuFilterNotificationDelivery.length > 0
    ) {
      filtered = filtered.filter((a) =>
        historyNotificationDeliveryRowMatchesFilter(
          a,
          menuFilterNotificationDelivery,
        ),
      );
    }

    if (!isHistoryZoneAllChecked && menuFilterTuneZones.length > 0) {
      filtered = filtered.filter((a) => {
        const roiId = String(a?.roiId ?? "").trim();
        if (roiId) return menuFilterTuneZones.includes(roiId);
        const z =
          a.zoneName != null && String(a.zoneName).trim() !== ""
            ? String(a.zoneName).trim()
            : "—";
        return selectedHistoryZoneNames.has(z);
      });
    }

    filtered.sort((a, b) => compareHistoryAlerts(a, b, orderBy, order));

    // No client-side pagination - API handles pagination
    return filtered;
  }, [
    allAlerts,
    searchQuery,
    isHistorySourceAllChecked,
    selectedSourceNames,
    mediaType,
    isHistoryPriorityAllChecked,
    filterPriority,
    orderBy,
    order,
    isHistoryAlertTypeAllChecked,
    menuFilterAlertTypes,
    isHistoryStatusAllChecked,
    menuFilterStatuses,
    isHistoryNotificationStatusAllChecked,
    menuFilterNotificationDelivery,
    isHistoryZoneAllChecked,
    menuFilterTuneZones,
    selectedHistoryZoneNames,
    localStatusOverrides,
  ]);

  const perPageForDisplay =
    paginationMeta.perPage > 0 ? paginationMeta.perPage : itemsPerPage;

  // Calculate total filtered alerts (before pagination) for pagination controls
  const totalFilteredAlerts = useMemo(() => {
    let filtered = [...allAlerts];

    if (searchQuery && String(searchQuery).trim()) {
      filtered = filtered.filter((a) =>
        historyAlertMatchesLocalSearch(a, searchQuery),
      );
    }

    if (!isHistorySourceAllChecked && selectedSourceNames.length > 0) {
      filtered = filtered.filter((a) =>
        selectedSourceNames.includes(a.sourceName)
      );
    }

    filtered = filtered.filter((a) => historyAlertMatchesMediaTab(a, mediaType));

    if (!isHistoryPriorityAllChecked && filterPriority.length > 0) {
      filtered = filtered.filter((a) =>
        historyAlertMatchesPriorityFilter(a, filterPriority),
      );
    }

    if (!isHistoryAlertTypeAllChecked && menuFilterAlertTypes.length > 0) {
      filtered = filtered.filter((a) =>
        historyAlertMatchesTuneAlertTypes(a, menuFilterAlertTypes),
      );
    }

    if (!isHistoryStatusAllChecked && menuFilterStatuses.length > 0) {
      filtered = filtered.filter((a) =>
        menuFilterStatuses.includes(
          getEffectiveWorkspaceStatus(a, localStatusOverrides),
        ),
      );
    }

    if (
      !isHistoryNotificationStatusAllChecked &&
      menuFilterNotificationDelivery.length > 0
    ) {
      filtered = filtered.filter((a) =>
        historyNotificationDeliveryRowMatchesFilter(
          a,
          menuFilterNotificationDelivery,
        ),
      );
    }

    if (!isHistoryZoneAllChecked && menuFilterTuneZones.length > 0) {
      filtered = filtered.filter((a) => {
        const roiId = String(a?.roiId ?? "").trim();
        if (roiId) return menuFilterTuneZones.includes(roiId);
        const z =
          a.zoneName != null && String(a.zoneName).trim() !== ""
            ? String(a.zoneName).trim()
            : "—";
        return selectedHistoryZoneNames.has(z);
      });
    }

    filtered.sort((a, b) => compareHistoryAlerts(a, b, orderBy, order));

    // No client-side pagination - API handles pagination
    return filtered;
  }, [
    allAlerts,
    searchQuery,
    isHistorySourceAllChecked,
    selectedSourceNames,
    mediaType,
    isHistoryPriorityAllChecked,
    filterPriority,
    orderBy,
    order,
    isHistoryAlertTypeAllChecked,
    menuFilterAlertTypes,
    isHistoryStatusAllChecked,
    menuFilterStatuses,
    isHistoryNotificationStatusAllChecked,
    menuFilterNotificationDelivery,
    isHistoryZoneAllChecked,
    menuFilterTuneZones,
    selectedHistoryZoneNames,
    localStatusOverrides,
  ]);

  // Calculate total pages - use API metadata if available, otherwise calculate from filtered alerts
  const totalPages =
    paginationMeta.totalPages > 0
      ? paginationMeta.totalPages
      : Math.ceil(
          totalFilteredAlerts.length / (perPageForDisplay || 1),
        );

  const totalCount =
    paginationMeta.total > 0
      ? paginationMeta.total
      : totalFilteredAlerts.length;

  /** When the list is empty but the user narrowed results (tabs, toolbar, or Tune menu), show "No Results" not onboarding. */
  const showHistoryFilteredEmptyState = useMemo(
    () =>
      Boolean(searchQuery?.trim()) ||
      !isHistoryPriorityAllChecked ||
      !isHistoryAlertTypeAllChecked ||
      !isHistoryStatusAllChecked ||
      !isHistoryNotificationStatusAllChecked ||
      !isHistoryZoneAllChecked ||
      !isHistorySourceAllChecked ||
      mediaType !== "all",
    [
      searchQuery,
      isHistoryPriorityAllChecked,
      isHistoryAlertTypeAllChecked,
      isHistoryStatusAllChecked,
      isHistoryNotificationStatusAllChecked,
      isHistoryZoneAllChecked,
      isHistorySourceAllChecked,
      mediaType,
    ],
  );

  /** Detail dialog: carousel slides + bottom-right thumbnail chip (AI Detection dashboard pattern). */
  const alertDetailMedia = useMemo(() => {
    if (!selectedAlert) {
      return {
        slides: [],
        thumbUrl: "",
        thumbnailSlideIndex: -1,
        showDetailsThumbnail: false,
      };
    }
    const rawSlides = buildAlertHistoryCarouselSlides(selectedAlert);
    const slides = filterHistoryDetailSlidesForMediaTab(rawSlides, mediaType);
    const thumbUrl = String(selectedAlert.thumbnailCropUrl || "").trim();
    const thumbnailSlideIndex = thumbUrl
      ? slides.findIndex(
          (s) =>
            s.kind === "image" && String(s.url || "").trim() === thumbUrl,
        )
      : -1;
    /** Plate/face crop chip: hide on VIDEO-only detail; stills tab can show image crops. */
    const showDetailsThumbnail =
      mediaType !== "video" &&
      Boolean(thumbUrl) &&
      historyAlertTypeUsesDetectionCropThumbnail(selectedAlert);
    return {
      slides,
      thumbUrl,
      thumbnailSlideIndex,
      showDetailsThumbnail,
    };
  }, [selectedAlert, mediaType]);

  /** Bottom-right crop: hide chrome until image loads (avoid empty white box on API failure). */
  const [historyDetailThumbLoaded, setHistoryDetailThumbLoaded] =
    useState(false);

  useEffect(() => {
    setHistoryDetailThumbLoaded(false);
  }, [
    detailDialogOpen,
    selectedAlert?.id,
    selectedAlert?.thumbnailCropUrl,
  ]);

  useEffect(() => {
    if (!detailDialogOpen) return undefined;
    const n = alertDetailMedia.slides.length;
    if (n < 2) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setHistoryDetailCarouselIndex((i) => (i - 1 + n) % n);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setHistoryDetailCarouselIndex((i) => (i + 1) % n);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailDialogOpen, alertDetailMedia.slides]);

  // Reset to page 1 when itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    mediaType,
    dateRange,
    startDate,
    endDate,
    selectedSourceNames,
    isHistoryPriorityAllChecked,
    filterPriority,
    menuFilterAlertTypes,
    isHistoryStatusAllChecked,
    menuFilterStatuses,
    isHistoryNotificationStatusAllChecked,
    menuFilterNotificationDelivery,
    menuFilterTuneZones,
  ]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const openHistoryRowMenu = (event, alert) => {
    event.stopPropagation();
    setHistoryRowMenuPointerPosition(null);
    setHistoryRowMenuAnchor(event.currentTarget);
    setHistoryRowMenuAlert(alert);
  };

  const closeHistoryRowMenu = () => {
    setHistoryRowMenuAnchor(null);
    setHistoryRowMenuPointerPosition(null);
    setHistoryRowMenuAlert(null);
  };

  /** Capture phase: runs before video/img/button children so preventDefault blocks the browser menu. */
  const handleAlertHistoryRowContextMenu = useCallback((e, alert) => {
    e.preventDefault();
    e.stopPropagation();
    setHistoryRowMenuAnchor(null);
    setHistoryRowMenuPointerPosition({
      top: e.clientY,
      left: e.clientX,
    });
    setHistoryRowMenuAlert(alert);
  }, []);

  const handleHistoryRowAcknowledge = async () => {
    if (!historyRowMenuAlert) return;
    const id = historyRowMenuAlert.id;
    try {
      await alertsHistoryAPI.patchAlertStatus(id, "acknowledged");
      setLocalStatusOverrides((prev) => ({
        ...prev,
        [id]: "Acknowledged",
      }));
      closeHistoryRowMenu();
      void fetchAlertHistoryRef.current?.();
    } catch (e) {
      console.error("Acknowledge alert failed:", e);
    }
  };

  const handleHistoryRowClose = async () => {
    if (!historyRowMenuAlert) return;
    const id = historyRowMenuAlert.id;
    try {
      await alertsHistoryAPI.patchAlertStatus(id, "closed");
      setLocalStatusOverrides((prev) => ({
        ...prev,
        [id]: "Closed",
      }));
      closeHistoryRowMenu();
      void fetchAlertHistoryRef.current?.();
    } catch (e) {
      console.error("Close alert failed:", e);
    }
  };

  const effectiveWorkspaceStatus = (alert) =>
    getEffectiveWorkspaceStatus(alert, localStatusOverrides);

  const historyStatusChipSx = (status) => {
    switch (status) {
      case "Acknowledged":
        return { backgroundColor: "#1B5E20", color: "#fff", fontWeight: 600 };
      case "Closed":
        return { backgroundColor: "#1565C0", color: "#fff", fontWeight: 600 };
      default:
        return { backgroundColor: "#FF9800", color: "#fff", fontWeight: 600 };
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "24px",
        boxShadow:
          "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: "max(400px, calc(100vh - 200px))",
        marginLeft: "10px",
        marginRight: "10px",
      }}
    >
      {/* Title row — matches Alert Rules breadcrumb strip */}
      <Box
        sx={{
          backgroundColor: "#fafafa",
          px: 2,
          py: 1,
          borderRadius: "20px 20px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ "& li": { display: "flex" } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              letterSpacing: "0.00938em",
              color: "#212121",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 700,
              fontSize: "16px",
              lineHeight: "22.88px",
            }}
          >
            <HistoryTabIcon sx={{ color: "#2932E5", fontSize: 22 }} />
            <Typography fontWeight={600} sx={{ color: "#212121" }}>
              Alert History
            </Typography>
          </Box>
        </Breadcrumbs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="History settings">
            <IconButton
              size="small"
              sx={{ color: "#666" }}
              onClick={() => setShowSettingsDialog(true)}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh list">
            <IconButton
              size="small"
              onClick={handleRefreshHistoryClick}
              aria-label="Refresh alert history"
              sx={{ color: "#666" }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Toolbar — aligned with Alert Rules controls */}
      <Box
        sx={{
          backgroundColor: "#fafafa",
          px: 1,
          py: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderBottom: "0.8px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            sx={{ flexWrap: "wrap", flex: 1, minWidth: 0 }}
          >
            <TextField
              placeholder="Search alerts"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchQuery(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: "1 1 200px",
                minWidth: 200,
                maxWidth: 400,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "white",
                  height: "36px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2932E5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#9CA3AF",
                  opacity: 1,
                },
              }}
            />

            <IconButton
              onClick={() => setViewMode("list")}
              size="small"
              aria-label="List view"
              sx={{
                borderRadius: "8px",
                color: viewMode === "list" ? "#2932E5" : "#6B7280",
                backgroundColor:
                  viewMode === "list" ? "rgba(41, 50, 229, 0.08)" : "white",
                border:
                  viewMode === "list"
                    ? "1px solid rgba(41, 50, 229, 0.35)"
                    : "1px solid #e5e7eb",
                width: 36,
                height: 36,
                "&:hover": {
                  backgroundColor:
                    viewMode === "list"
                      ? "rgba(41, 50, 229, 0.12)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setViewMode("grid")}
              size="small"
              aria-label="Grid view"
              sx={{
                borderRadius: "8px",
                color: viewMode === "grid" ? "#2932E5" : "#6B7280",
                backgroundColor:
                  viewMode === "grid" ? "rgba(41, 50, 229, 0.08)" : "white",
                border:
                  viewMode === "grid"
                    ? "1px solid rgba(41, 50, 229, 0.35)"
                    : "1px solid #e5e7eb",
                width: 36,
                height: 36,
                "&:hover": {
                  backgroundColor:
                    viewMode === "grid"
                      ? "rgba(41, 50, 229, 0.12)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>

            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setDateRangeMenuAnchor(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              aria-haspopup="listbox"
              aria-expanded={Boolean(dateRangeMenuAnchor)}
              aria-label="Date range"
              sx={historyToolbarFilterButtonSx(dateRangeMenuAnchor)}
            >
              {getDateRangeDisplay()}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setSourceNameMenuAnchor(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              aria-label="Sub-source"
              sx={historyToolbarFilterButtonSx(sourceNameMenuAnchor)}
            >
              {getSourceNameDisplay()}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setZoneNameMenuAnchor(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              aria-label="Zone"
              sx={historyToolbarFilterButtonSx(zoneNameMenuAnchor)}
            >
              {getZoneToolbarDisplay()}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setAlertTypeToolbarMenuAnchor(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              aria-label="Alert Type"
              sx={historyToolbarFilterButtonSx(alertTypeToolbarMenuAnchor)}
            >
              {getAlertTypeToolbarDisplay()}
            </Button>

          <ToggleButtonGroup
            value={mediaType}
            exclusive
            onChange={(e, val) => {
              if (val !== null) {
                setMediaType(val);
              }
            }}
            size="small"
          >
            <ToggleButton
              value="all"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              ALL
            </ToggleButton>
            <ToggleButton
              value="video"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              VIDEO
            </ToggleButton>
            <ToggleButton
              value="photo"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              PHOTO
            </ToggleButton>
          </ToggleButtonGroup>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                (() => {
                  const parts = [];
                  if (!isHistoryPriorityAllChecked && filterPriority.length > 0) {
                    parts.push(`Priority: ${filterPriority.join(", ")}`);
                  } else if (!isHistoryPriorityAllChecked) {
                    parts.push("Priority: none");
                  }
                  if (selectedSourceNames.length > 0) {
                    parts.push(`Source: ${selectedSourceNames.join(", ")}`);
                  }
                  if (menuFilterTuneZones.length > 0) {
                    const labels = menuFilterTuneZones.map((id) => {
                      const hit = zoneToolbarOptions.find((z) => z.value === id);
                      return hit?.label ?? String(id);
                    });
                    parts.push(`Zone: ${labels.join(", ")}`);
                  }
                  if (menuFilterAlertTypeLabels.length > 0) {
                    parts.push(
                      `Alert type: ${menuFilterAlertTypeLabels.join(", ")}`,
                    );
                  }
                  if (!isHistoryStatusAllChecked && menuFilterStatuses.length > 0) {
                    parts.push(
                      `Alert status: ${menuFilterStatuses.join(", ")}`,
                    );
                  } else if (!isHistoryStatusAllChecked) {
                    parts.push("Alert status: none");
                  }
                  if (
                    !isHistoryNotificationStatusAllChecked &&
                    menuFilterNotificationDelivery.length > 0
                  ) {
                    const labels = menuFilterNotificationDelivery.map((v) => {
                      const hit =
                        HISTORY_NOTIFICATION_DELIVERY_FILTER_OPTIONS.find(
                          (o) => o.value === String(v).toLowerCase(),
                        );
                      return hit?.label ?? v;
                    });
                    parts.push(`Notification status: ${labels.join(", ")}`);
                  } else if (!isHistoryNotificationStatusAllChecked) {
                    parts.push("Notification status: none");
                  }
                  return parts.length > 0
                    ? parts.join(" · ")
                    : "Filters: priority, alert status, notification status (toolbar: date, source, zone, alert type)";
                })()
              }
            >
              <IconButton
                size="small"
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                aria-label="Open filters"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: filterMenuAnchor ? "#2932E5" : "#6B7280",
                  backgroundColor: "white",
                  border: filterMenuAnchor
                    ? "1px solid #2932E5"
                    : "1px solid #e5e7eb",
                  "&:hover": {
                    backgroundColor: "rgba(41, 50, 229, 0.06)",
                    borderColor: "#2932E5",
                  },
                }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      {/* Sub-source menu */}
      <Menu
        anchorEl={sourceNameMenuAnchor}
        open={Boolean(sourceNameMenuAnchor)}
        onClose={() => setSourceNameMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 300, width: 250 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            placeholder="Search source names..."
            size="small"
            fullWidth
            value={sourceNameSearch}
            onChange={(e) => setSourceNameSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            if (isHistorySourceAllChecked) {
              setIsHistorySourceAllChecked(false);
              setSelectedSourceNames([]);
            } else {
              setIsHistorySourceAllChecked(true);
              setSelectedSourceNames([]);
            }
            setCurrentPage(1);
          }}
          sx={{ fontFamily: "Roboto, sans-serif", color: "#2932E5" }}
        >
          {isHistorySourceAllChecked ? "All clear" : "All check"}
        </MenuItem>
        {historyCamerasLoading ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Loading cameras…
          </MenuItem>
        ) : null}
        {!historyCamerasLoading && historyCamerasError ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Could not load cameras
          </MenuItem>
        ) : null}
        {!historyCamerasLoading &&
        !historyCamerasError &&
        filteredSourceNames.length === 0 ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            No source names available
          </MenuItem>
        ) : null}
        {!historyCamerasLoading &&
        !historyCamerasError &&
        filteredSourceNames.map((name) => (
          <MenuItem
            key={name}
            onClick={() => {
              if (isHistorySourceAllChecked) {
                setIsHistorySourceAllChecked(false);
                setSelectedSourceNames([name]);
                setCurrentPage(1);
                return;
              }
              const next = selectedSourceNames.includes(name)
                ? selectedSourceNames.filter((n) => n !== name)
                : [...selectedSourceNames, name];
              if (next.length === uniqueSourceNames.length) {
                setIsHistorySourceAllChecked(true);
                setSelectedSourceNames([]);
              } else {
                setIsHistorySourceAllChecked(false);
                setSelectedSourceNames(next);
              }
              setCurrentPage(1);
            }}
            sx={{ fontFamily: "Roboto, sans-serif" }}
          >
            <Checkbox
              checked={
                isHistorySourceAllChecked || selectedSourceNames.includes(name)
              }
              size="small"
            />
            {name}
          </MenuItem>
        ))}
      </Menu>

      {/* Zone (toolbar — same state as previous Tune zone filter) */}
      <Menu
        anchorEl={zoneNameMenuAnchor}
        open={Boolean(zoneNameMenuAnchor)}
        onClose={() => setZoneNameMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 300, width: 260 },
        }}
      >
        <MenuItem
          onClick={() => {
            if (isHistoryZoneAllChecked) {
              setIsHistoryZoneAllChecked(false);
              setMenuFilterTuneZones([]);
            } else {
              setIsHistoryZoneAllChecked(true);
              setMenuFilterTuneZones([]);
            }
            setCurrentPage(1);
          }}
          sx={{ fontFamily: "Roboto, sans-serif", color: "#2932E5" }}
        >
          {isHistoryZoneAllChecked ? "All clear" : "All check"}
        </MenuItem>
        {historyZonesLoading ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Loading zones…
          </MenuItem>
        ) : historyZonesError ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Could not load zones
          </MenuItem>
        ) : zoneToolbarOptions.length === 0 ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Load alerts to filter by zone
          </MenuItem>
        ) : (
          zoneToolbarOptions.map((zone) => (
            <MenuItem
              key={zone.value}
              onClick={() => {
                setCurrentPage(1);
                if (isHistoryZoneAllChecked) {
                  setIsHistoryZoneAllChecked(false);
                  setMenuFilterTuneZones([zone.value]);
                  return;
                }
                const next = menuFilterTuneZones.includes(zone.value)
                  ? menuFilterTuneZones.filter((x) => x !== zone.value)
                  : [...menuFilterTuneZones, zone.value];
                if (next.length === zoneToolbarOptions.length) {
                  setIsHistoryZoneAllChecked(true);
                  setMenuFilterTuneZones([]);
                } else {
                  setIsHistoryZoneAllChecked(false);
                  setMenuFilterTuneZones(next);
                }
              }}
              sx={{ fontFamily: "Roboto, sans-serif" }}
            >
              <Checkbox
                checked={
                  isHistoryZoneAllChecked ||
                  menuFilterTuneZones.includes(zone.value)
                }
                size="small"
              />
              {zone.label}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Alert Type (toolbar) */}
      <Menu
        anchorEl={alertTypeToolbarMenuAnchor}
        open={Boolean(alertTypeToolbarMenuAnchor)}
        onClose={() => setAlertTypeToolbarMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 320, width: 280 },
        }}
      >
        <MenuItem
          onClick={() => {
            if (isHistoryAlertTypeAllChecked) {
              setIsHistoryAlertTypeAllChecked(false);
              setMenuFilterAlertTypes([]);
            } else {
              setIsHistoryAlertTypeAllChecked(true);
              setMenuFilterAlertTypes([]);
            }
            setCurrentPage(1);
          }}
          sx={{ fontFamily: "Roboto, sans-serif", color: "#2932E5" }}
        >
          {isHistoryAlertTypeAllChecked ? "All clear" : "All check"}
        </MenuItem>
        {useCasesLoading ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Loading use cases…
          </MenuItem>
        ) : useCasesError ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            Could not load use cases
          </MenuItem>
        ) : alertTypeTuneMenuOptions.length === 0 ? (
          <MenuItem disabled sx={{ fontFamily: "Roboto, sans-serif" }}>
            No alert types — adjust date range or load alerts
          </MenuItem>
        ) : (
          alertTypeTuneMenuOptions.map(({ value, label }) => (
            <MenuItem
              key={String(value)}
              onClick={() => {
                setCurrentPage(1);
                const optionValues = alertTypeTuneMenuOptions.map(
                  (x) => x.value,
                );
                if (isHistoryAlertTypeAllChecked) {
                  setIsHistoryAlertTypeAllChecked(false);
                  setMenuFilterAlertTypes([value]);
                  return;
                }
                const next = menuFilterAlertTypes.includes(value)
                  ? menuFilterAlertTypes.filter((x) => x !== value)
                  : [...menuFilterAlertTypes, value];
                if (next.length === optionValues.length) {
                  setIsHistoryAlertTypeAllChecked(true);
                  setMenuFilterAlertTypes([]);
                } else {
                  setIsHistoryAlertTypeAllChecked(false);
                  setMenuFilterAlertTypes(next);
                }
              }}
              sx={{ fontFamily: "Roboto, sans-serif" }}
            >
              <Checkbox
                checked={
                  isHistoryAlertTypeAllChecked ||
                  menuFilterAlertTypes.includes(value)
                }
                size="small"
              />
              {label}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Date Range Menu */}
      <Menu
        anchorEl={dateRangeMenuAnchor}
        open={Boolean(dateRangeMenuAnchor)}
        onClose={() => setDateRangeMenuAnchor(null)}
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        {[
          { value: "all", label: "All dates" },
          { value: "today", label: "Today" },
          { value: "yesterday", label: "Yesterday" },
          { value: "last7days", label: "Last 7 days" },
          { value: "last30days", label: "Last 30 days" },
          { value: "thisweek", label: "This week" },
          { value: "thismonth", label: "This month" },
          { value: "custom", label: "Custom range" },
        ].map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleDateRangeChange(option.value)}
            selected={dateRange === option.value}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              "&.Mui-selected": {
                backgroundColor: "rgba(41, 50, 229, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(41, 50, 229, 0.12)",
                },
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Filter Menu — same options for video and photo */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        disableAutoFocus
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: HISTORY_FILTER_MENU_PAPER_SX,
          onMouseDown: (e) => e.stopPropagation(),
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.87)",
              mb: 0.5,
            }}
          >
            Filters
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "Roboto, sans-serif",
              color: "text.secondary",
              display: "block",
            }}
          >
            Applies to Video and Photo
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.87)",
              mb: 1,
            }}
          >
            Priority
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            setCurrentPage(1);
            if (isHistoryPriorityAllChecked) {
              setIsHistoryPriorityAllChecked(false);
              setFilterPriority([]);
              return;
            }
            setIsHistoryPriorityAllChecked(true);
            setFilterPriority([]);
          }}
          sx={HISTORY_FILTER_MENU_ITEM_SX}
        >
          <Checkbox
            checked={isHistoryPriorityAllChecked}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={HISTORY_FILTER_MENU_CHECKBOX_SX}
          />
          <Typography component="span" sx={HISTORY_FILTER_MENU_LABEL_SX}>
            {isHistoryPriorityAllChecked ? "All clear" : "All check"}
          </Typography>
        </MenuItem>
        {["Critical", "High", "Medium", "Low"].map((priority) => (
          <MenuItem
            key={priority}
            onClick={() => {
              setCurrentPage(1);
              const priorityOptions = ["Critical", "High", "Medium", "Low"];
              if (isHistoryPriorityAllChecked) {
                setIsHistoryPriorityAllChecked(false);
                setFilterPriority([priority]);
                return;
              }
              setFilterPriority((prev) => {
                const next = prev.includes(priority)
                  ? prev.filter((p) => p !== priority)
                  : [...prev, priority];
                const allSelected = priorityOptions.every((option) =>
                  next.includes(option),
                );
                if (allSelected) {
                  setIsHistoryPriorityAllChecked(true);
                  return [];
                }
                return next;
              });
            }}
            sx={HISTORY_FILTER_MENU_ITEM_SX}
          >
            <Checkbox
              checked={
                isHistoryPriorityAllChecked || filterPriority.includes(priority)
              }
              size="small"
              tabIndex={-1}
              disableRipple
              sx={HISTORY_FILTER_MENU_CHECKBOX_SX}
            />
            <Typography component="span" sx={HISTORY_FILTER_MENU_LABEL_SX}>
              {priority}
            </Typography>
          </MenuItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.87)",
              mb: 1,
            }}
          >
            Status
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            setCurrentPage(1);
            if (isHistoryStatusAllChecked) {
              setIsHistoryStatusAllChecked(false);
              setMenuFilterStatuses([]);
              return;
            }
            setIsHistoryStatusAllChecked(true);
            setMenuFilterStatuses([]);
          }}
          sx={HISTORY_FILTER_MENU_ITEM_SX}
        >
          <Checkbox
            checked={isHistoryStatusAllChecked}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={HISTORY_FILTER_MENU_CHECKBOX_SX}
          />
          <Typography component="span" sx={HISTORY_FILTER_MENU_LABEL_SX}>
            {isHistoryStatusAllChecked ? "All clear" : "All check"}
          </Typography>
        </MenuItem>
        {HISTORY_STATUS_FILTER_OPTIONS.map((st) => (
          <MenuItem
            key={st}
            onClick={() => {
              setCurrentPage(1);
              if (isHistoryStatusAllChecked) {
                setIsHistoryStatusAllChecked(false);
                setMenuFilterStatuses([st]);
                return;
              }
              setMenuFilterStatuses((prev) => {
                const next = prev.includes(st)
                  ? prev.filter((x) => x !== st)
                  : [...prev, st];
                const allSelected = HISTORY_STATUS_FILTER_OPTIONS.every((item) =>
                  next.includes(item),
                );
                if (allSelected) {
                  setIsHistoryStatusAllChecked(true);
                  return [];
                }
                return next;
              });
            }}
            sx={HISTORY_FILTER_MENU_ITEM_SX}
          >
            <Checkbox
              checked={isHistoryStatusAllChecked || menuFilterStatuses.includes(st)}
              size="small"
              tabIndex={-1}
              disableRipple
              sx={HISTORY_FILTER_MENU_CHECKBOX_SX}
            />
            <Typography component="span" sx={HISTORY_FILTER_MENU_LABEL_SX}>
              {st}
            </Typography>
          </MenuItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.87)",
              mb: 1,
            }}
          >
            Notification status
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            setCurrentPage(1);
            if (isHistoryNotificationStatusAllChecked) {
              setIsHistoryNotificationStatusAllChecked(false);
              setMenuFilterNotificationDelivery([]);
              return;
            }
            setIsHistoryNotificationStatusAllChecked(true);
            setMenuFilterNotificationDelivery([]);
          }}
          sx={HISTORY_FILTER_MENU_ITEM_SX}
        >
          <Checkbox
            checked={isHistoryNotificationStatusAllChecked}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={HISTORY_FILTER_MENU_CHECKBOX_SX}
          />
          <Typography component="span" sx={HISTORY_FILTER_MENU_LABEL_SX}>
            {isHistoryNotificationStatusAllChecked ? "All clear" : "All check"}
          </Typography>
        </MenuItem>
        {HISTORY_NOTIFICATION_DELIVERY_FILTER_OPTIONS.map(({ value, label }) => (
          <MenuItem
            key={value}
            onClick={() => {
              setCurrentPage(1);
              const notificationStatusOptions =
                HISTORY_NOTIFICATION_DELIVERY_FILTER_OPTIONS.map((item) =>
                  String(item.value).toLowerCase(),
                );
              if (isHistoryNotificationStatusAllChecked) {
                setIsHistoryNotificationStatusAllChecked(false);
                setMenuFilterNotificationDelivery([
                  String(value).toLowerCase(),
                ]);
                return;
              }
              setMenuFilterNotificationDelivery((prev) => {
                const normalizedValue = String(value).toLowerCase();
                const next = prev.includes(normalizedValue)
                  ? prev.filter((x) => x !== normalizedValue)
                  : [...prev, normalizedValue];
                const allSelected = notificationStatusOptions.every((item) =>
                  next.includes(item),
                );
                if (allSelected) {
                  setIsHistoryNotificationStatusAllChecked(true);
                  return [];
                }
                return next;
              });
            }}
            sx={HISTORY_FILTER_MENU_ITEM_SX}
          >
            <Checkbox
              checked={
                isHistoryNotificationStatusAllChecked ||
                menuFilterNotificationDelivery.includes(String(value).toLowerCase())
              }
              size="small"
              tabIndex={-1}
              disableRipple
              sx={HISTORY_FILTER_MENU_CHECKBOX_SX}
            />
            <Typography component="span" sx={HISTORY_FILTER_MENU_LABEL_SX}>
              {label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Grid view — card layout aligned with Manage Use Case (ManageUseCaseTable) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loadingAlerts ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "min(400px, calc(100vh - 280px))",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
        {filteredAlerts.length === 0 && hasApiResponse ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              minHeight: HISTORY_LIST_VIEW_HEIGHT,
            }}
          >
            <EmptyState
              type={
                showHistoryFilteredEmptyState ? "no-results" : undefined
              }
              sx={{
                flex: 1,
                width: "100%",
                minHeight: "100%",
              }}
            />
          </Box>
        ) : filteredAlerts.length === 0 && !hasApiResponse ? null : viewMode ===
          "grid" ? (
          <Box
            sx={{
              height: HISTORY_LIST_VIEW_HEIGHT,
              maxHeight: HISTORY_LIST_VIEW_HEIGHT,
              minHeight: 0,
              overflow: "auto",
              p: { xs: 0.5, sm: 0 },
            }}
          >
            <Grid container spacing={1}>
              {filteredAlerts.map((alert) => {
                const getStatusColor = (status) => {
                  if (status === "Acknowledged") return "#1B5E20";
                  if (status === "Closed" || status === "Resolved")
                    return "#1565C0";
                  if (status === "Active") return "#FF9800";
                  return "#9E9E9E";
                };

                const cardStatus = effectiveWorkspaceStatus(alert);
                const typeLabel = (alert.alertType || "ALERT")
                  .replace(/_/g, " ")
                  .toUpperCase();
                const hero = historyGridHeroMode(alert, mediaType);
                const showVideoHero =
                  hero.kind === "video" && Boolean(hero.videoHref);
                const showPhotoHero =
                  hero.kind === "image" && Boolean(hero.photoHref);
                const channels =
                  alert.notifyChannels?.length > 0
                    ? alert.notifyChannels
                    : normalizeNotifyChannelStrings(alert.actionTypes || []);

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    xl={2}
                    key={alert.id}
                    sx={{ display: "flex", minWidth: 0 }}
                  >
                    <Card
                      onClick={() => {
                        setSelectedAlert(alert);
                        setDetailDialogOpen(true);
                      }}
                      onContextMenuCapture={(e) =>
                        handleAlertHistoryRowContextMenu(e, alert)
                      }
                      sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        minWidth: 0,
                        height: { xs: "228px", sm: "252px" },
                        minHeight: { xs: 208, sm: 248 },
                        bgcolor: "rgb(255, 255, 255)",
                        color: "rgb(33, 33, 33)",
                        boxShadow: "var(--Paper-shadow)",
                        backgroundImage: "var(--Paper-overlay)",
                        overflow: "hidden",
                        borderRadius: "14px",
                        transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        border: "1px solid rgb(229, 231, 235)",
                        "&:hover": {
                          boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                          transform: "translateY(-4px) scale(1.02)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          paddingLeft: { xs: 1, sm: "10px" },
                          paddingRight: { xs: 1, sm: "10px" },
                          pt: 1,
                          pb: 0,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            maxWidth: "100%",
                            height: { xs: "96px", sm: "112.5px" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgb(33, 33, 33)",
                            borderRadius: "8px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          {showVideoHero && !imageErrors[alert.id] ? (
                            <AlertHistoryMediaVideo
                              href={hero.videoHref}
                              poster={historyListThumbHref(alert) || undefined}
                              controls={false}
                              muted
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                              }}
                              videoSx={{
                                width: "100%",
                                height: "100%",
                                maxHeight: "none",
                                objectFit: "cover",
                                borderRadius: 0,
                              }}
                            />
                          ) : showPhotoHero && !imageErrors[alert.id] ? (
                            <AlertHistoryMediaImage
                              href={hero.photoHref}
                              alt=""
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                              }}
                              imgSx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                              onError={() => {
                                setImageErrors((prev) => ({
                                  ...prev,
                                  [alert.id]: true,
                                }));
                              }}
                            />
                          ) : (
                            <ImageIcon
                              sx={{
                                fontSize: 40,
                                color: "rgba(255,255,255,0.35)",
                              }}
                              aria-hidden
                            />
                          )}
                          <Chip
                            label={cardStatus}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 44,
                              zIndex: 1,
                              height: 24,
                              fontSize: "11px",
                              fontWeight: 600,
                              fontFamily: "Roboto, sans-serif",
                              backgroundColor: getStatusColor(cardStatus),
                              color: "#fff",
                            }}
                          />
                          {/* Row actions (3-dots) — same as list view */}
                          <IconButton
                            size="small"
                            aria-label="Row actions"
                            onClick={(e) => openHistoryRowMenu(e, alert)}
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              zIndex: 3,
                              width: 30,
                              height: 30,
                              color: "rgba(255,255,255,0.95)",
                              backgroundColor: "rgba(0,0,0,0.35)",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.5)",
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 10,
                              left: 10,
                              zIndex: 1,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: "20px",
                              bgcolor: "rgba(255, 179, 128, 0.92)",
                              color: "#fff",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "11px",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              textTransform: "uppercase",
                              maxWidth: "calc(100% - 24px)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeLabel}
                          </Box>
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              zIndex: 2,
                              color: "rgba(255,255,255,0.95)",
                              display: "flex",
                              alignItems: "center",
                              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                            }}
                          >
                            {mediaType === "photo" ? (
                              <ImageIcon sx={{ fontSize: 20 }} />
                            ) : mediaType === "video" ? (
                              <PlayArrowIcon sx={{ fontSize: 20 }} />
                            ) : alert.hasVideo ? (
                              <PlayArrowIcon sx={{ fontSize: 20 }} />
                            ) : (
                              <ImageIcon sx={{ fontSize: 20 }} />
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.5 },
                          pt: { xs: 1, sm: 1.25 },
                          flex: 1,
                          minWidth: 0,
                          minHeight: { xs: 72, sm: 88 },
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          overflow: "hidden",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: { xs: "14px", sm: "16px" },
                            fontWeight: 700,
                            color: "#212121",
                            mb: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {alert.alertName || "Unknown Alert"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: { xs: "12px", sm: "13px" },
                            color: "#4A5565",
                            mb: 0.25,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {alert.srId != null && String(alert.srId).trim() !== ""
                            ? `ID: ${String(alert.srId).trim()} · `
                            : ""}
                          {formatHistoryDateChip(alert.timestamp)} ·{" "}
                          {alert.sourceName || "—"}
                        </Typography>
                        <Typography
                          component="div"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "10px",
                            fontWeight: 600,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            mb: 0.25,
                            mt: 0.25,
                          }}
                        >
                          Notified by
                        </Typography>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                          <AlertNotifiedByChips
                            row={alert}
                            channels={channels}
                            notificationStatus={alert.notificationStatusRaw}
                            stackSx={{
                              maxWidth: "100%",
                              flexWrap: "wrap",
                              overflow: "hidden",
                              "& .MuiStack-root": {
                                rowGap: 0.25,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : (
          <TableContainer
            sx={{ height: HISTORY_LIST_VIEW_HEIGHT, overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{ tableLayout: "auto", width: "100%", minWidth: 1180 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...HISTORY_TABLE_HEADER_SX,
                      width: 88,
                      maxWidth: 120,
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Alert Name</TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Severity</TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>
                    <TableSortLabel
                      active={orderBy === "timestamp"}
                      direction={orderBy === "timestamp" ? order : "asc"}
                      onClick={() => handleRequestSort("timestamp")}
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        color: "#2932E5 !important",
                        "&.Mui-active": { color: "#2932E5 !important" },
                        "& .MuiTableSortLabel-icon": {
                          color: "#2932E5 !important",
                        },
                      }}
                    >
                      Date & Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Alert Type</TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>
                    <TableSortLabel
                      active={orderBy === "sourceName"}
                      direction={orderBy === "sourceName" ? order : "asc"}
                      onClick={() => handleRequestSort("sourceName")}
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        color: "#2932E5 !important",
                        "&.Mui-active": { color: "#2932E5 !important" },
                        "& .MuiTableSortLabel-icon": {
                          color: "#2932E5 !important",
                        },
                      }}
                    >
                      Source
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Zone</TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Status</TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Notified To</TableCell>
                  <TableCell sx={HISTORY_TABLE_HEADER_SX}>Notified By</TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...HISTORY_TABLE_HEADER_SX, pr: 2 }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAlerts.length === 0 && hasApiResponse ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <EmptyState
                        type={
                          showHistoryFilteredEmptyState
                            ? "no-results"
                            : undefined
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : filteredAlerts.length === 0 && !hasApiResponse ? null : (
                  filteredAlerts.map((alert) => {
                    const rowStatus = effectiveWorkspaceStatus(alert);
                    const channels =
                      alert.notifyChannels?.length > 0
                        ? alert.notifyChannels
                        : normalizeNotifyChannelStrings(alert.actionTypes || []);
                    const listPreview = historyListRowPreview(alert, mediaType);
                    const listThumbPoster = historyListThumbHref(alert);
                    return (
                      <TableRow
                        key={alert.id}
                        hover
                        onMouseEnter={() => setHoveredRow(alert.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        onClick={() => {
                          setSelectedAlert(alert);
                          setDetailDialogOpen(true);
                        }}
                        onContextMenuCapture={(e) =>
                          handleAlertHistoryRowContextMenu(e, alert)
                        }
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.8125rem",
                            color: "#4A5565",
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={
                            alert.srId != null && String(alert.srId).trim() !== ""
                              ? String(alert.srId)
                              : undefined
                          }
                        >
                          {alert.srId != null && String(alert.srId).trim() !== ""
                            ? String(alert.srId).trim()
                            : "—"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {alert.alertName || "Unknown Alert"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          {alert.severityLabel ?? formatSeverityDisplay(alert.severity)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={formatHistoryDateChip(alert.timestamp)}
                            size="small"
                            sx={{
                              height: 26,
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              backgroundColor: "#FFE0B2",
                              color: "rgba(0,0,0,0.87)",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          {alert.alertType ?? "—"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: 44,
                                height: 30,
                                flexShrink: 0,
                                borderRadius: "4px",
                                overflow: "hidden",
                                border: "1px solid #E5E7EB",
                                bgcolor: "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {listPreview.mode === "image" && listPreview.href ? (
                                <AlertHistoryMediaImage
                                  href={listPreview.href}
                                  alt=""
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "block",
                                  }}
                                  imgSx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              ) : listPreview.mode === "video" &&
                                listPreview.href ? (
                                <AlertHistoryMediaVideo
                                  href={listPreview.href}
                                  poster={
                                    listThumbPoster &&
                                    listThumbPoster !== listPreview.href
                                      ? listThumbPoster
                                      : undefined
                                  }
                                  controls={false}
                                  muted
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "block",
                                  }}
                                  videoSx={{
                                    width: "100%",
                                    height: "100%",
                                    maxHeight: "none",
                                    objectFit: "cover",
                                    borderRadius: 0,
                                  }}
                                />
                              ) : (
                                <ImageIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "rgba(0,0,0,0.26)",
                                  }}
                                  aria-hidden
                                />
                              )}
                              {mediaType === "video" &&
                              alert.hasVideo &&
                              (listPreview.mode === "image" ||
                                listPreview.mode === "video") &&
                              listPreview.href ? (
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    pointerEvents: "none",
                                    bgcolor: "rgba(0,0,0,0.18)",
                                  }}
                                >
                                  <PlayArrowIcon
                                    sx={{
                                      fontSize: 18,
                                      color: "rgba(255,255,255,0.95)",
                                      filter:
                                        "drop-shadow(0 1px 2px rgba(0,0,0,0.55))",
                                    }}
                                  />
                                </Box>
                              ) : null}
                            </Box>
                            {mediaType === "all" && alert.hasVideo ? (
                              <VideocamIcon
                                sx={{
                                  fontSize: 18,
                                  color: "rgba(0,0,0,0.54)",
                                }}
                              />
                            ) : null}
                            <span>{alert.sourceName ?? "—"}</span>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "0.875rem",
                          }}
                        >
                          {alert.zoneName ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rowStatus}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: "0.75rem",
                              ...historyStatusChipSx(rowStatus),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography
                            noWrap
                            title={String(alert.notifiedTo ?? "—")}
                            sx={{ fontSize: "12px" }}
                          >
                            {alert.notifiedTo ?? "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <AlertNotifiedByChips
                            row={alert}
                            channels={channels}
                            notificationStatus={alert.notificationStatusRaw}
                            stackSx={{
                              maxWidth: 220,
                              flexWrap: "nowrap",
                              overflow: "hidden",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ pr: 0.5, verticalAlign: "middle" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            aria-label="Row actions"
                            onClick={(e) => openHistoryRowMenu(e, alert)}
                            sx={{ color: "rgba(0,0,0,0.54)" }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#f2f2f2",
            padding: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              Items per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setItemsPerPage(next);
                  setCurrentPage(1);
                  persistAlertHistoryDefaultPagination(next);
                }}
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  height: "32px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
              >
                <MenuItem value={10} sx={{ fontFamily: "Roboto, sans-serif" }}>
                  10
                </MenuItem>
                <MenuItem value={25} sx={{ fontFamily: "Roboto, sans-serif" }}>
                  25
                </MenuItem>
                <MenuItem value={50} sx={{ fontFamily: "Roboto, sans-serif" }}>
                  50
                </MenuItem>
              </Select>
            </FormControl>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.6)",
              }}
            >
              {totalCount > 0
                ? `${(currentPage - 1) * perPageForDisplay + 1}-${Math.min(
                    currentPage * perPageForDisplay,
                    totalCount,
                  )} of ${totalCount}`
                : `0 of 0`}
            </Typography>
          </Box>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
              },
              "& .Mui-selected": {
                backgroundColor: "#2932E5",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1f28c7",
                },
              },
            }}
          />
        </Box>
          </>
        )}
      </Box>

      {/* Detail Dialog — close only via header X (not backdrop / Escape) */}
      <Dialog
        open={detailDialogOpen}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            margin: "0px",
            lineHeight: "1.6",
            letterSpacing: "0.0075em",
            padding: "16px 24px",
            flex: "0 0 auto",
            fontFamily: "Roboto, sans-serif",
            fontSize: "20px",
            color: "rgb(255, 133, 85)",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <span>Alert Details</span>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={() => setDetailDialogOpen(false)}
            sx={{ color: "#4A5565" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: "Roboto, sans-serif",
            padding: 0,
          }}
        >
          {selectedAlert && (() => {
            const { slides, thumbUrl, thumbnailSlideIndex, showDetailsThumbnail } =
              alertDetailMedia;
            const thumbUiVisible =
              showDetailsThumbnail && historyDetailThumbLoaded;
            const n = slides.length;
            const carouselIndex =
              n === 0
                ? 0
                : ((historyDetailCarouselIndex % n) + n) % n;
            const currentSlide = slides[carouselIndex];

            const renderOneSlide = (s, keySuffix = "") => {
              if (!s?.url) return null;
              if (s.kind === "video") {
                return (
                  <AlertHistoryMediaVideo
                    key={`${s.url}-${keySuffix}`}
                    href={s.url}
                    poster={thumbUrl && String(thumbUrl).trim() ? thumbUrl : undefined}
                    videoSx={{
                      width: "100%",
                      maxHeight: { xs: 240, sm: 320 },
                      objectFit: "contain",
                    }}
                  />
                );
              }
              return (
                <AlertHistoryMediaImage
                  key={`${s.url}-${keySuffix}`}
                  href={s.url}
                  alt=""
                  sx={{ width: "100%", display: "block", maxWidth: "100%" }}
                  imgSx={{
                    display: "block",
                    width: "100%",
                    maxHeight: { xs: 240, sm: 320 },
                    objectFit: "contain",
                  }}
                />
              );
            };

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <Box
                  ref={historyDetailMediaAreaRef}
                  sx={{
                    bgcolor: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 200,
                    maxHeight: { xs: 280, sm: 360 },
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  {n === 0 ? (
                    <ImageIcon
                      sx={{ fontSize: 56, color: "rgba(255,255,255,0.25)" }}
                      aria-hidden
                    />
                  ) : n === 1 ? (
                    renderOneSlide(slides[0], "single")
                  ) : (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        minHeight: { xs: 200, sm: 260 },
                        maxHeight: { xs: 260, sm: 340 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: { xs: 5, sm: 6 },
                        py: 1,
                      }}
                    >
                      <IconButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHistoryDetailCarouselIndex(
                            (i) => (i - 1 + n) % n,
                          );
                        }}
                        aria-label="Previous slide"
                        sx={{
                          position: "absolute",
                          left: 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 2,
                          color: "#fff",
                          bgcolor: "rgba(255,255,255,0.12)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                        }}
                        size="small"
                      >
                        <ChevronLeftIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                      {currentSlide
                        ? renderOneSlide(currentSlide, `c-${carouselIndex}`)
                        : null}
                      <IconButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHistoryDetailCarouselIndex((i) => (i + 1) % n);
                        }}
                        aria-label="Next slide"
                        sx={{
                          position: "absolute",
                          right: 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 2,
                          color: "#fff",
                          bgcolor: "rgba(255,255,255,0.12)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                        }}
                        size="small"
                      >
                        <ChevronRightIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          left: 0,
                          right: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 0.75,
                          zIndex: 2,
                        }}
                      >
                        {slides.map((_, i) => (
                          <Box
                            key={`history-carousel-dot-${i}`}
                            component="button"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHistoryDetailCarouselIndex(i);
                            }}
                            aria-label={`Go to slide ${i + 1}`}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              border: "none",
                              p: 0,
                              cursor: "pointer",
                              bgcolor:
                                i === carouselIndex
                                  ? "#fff"
                                  : "rgba(255,255,255,0.35)",
                              "&:hover": {
                                bgcolor:
                                  i === carouselIndex
                                    ? "#fff"
                                    : "rgba(255,255,255,0.55)",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    position: "relative",
                    p: 3,
                    /* Match AI Detection snapshot modal: room for bottom-right crop (80×56 / 104×72) */
                    pr: thumbUiVisible ? { xs: 2, sm: 15 } : 3,
                    pb: thumbUiVisible ? { xs: 2, sm: 2.5 } : 3,
                    bgcolor: "#fafafa",
                    borderTop: "1px solid #E5E7EB",
                    overflow: "visible",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 12,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                      mb: 1,
                    }}
                  >
                    Details
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      mb: 1.5,
                      pr: thumbUiVisible ? { xs: 0, sm: 1 } : 0,
                    }}
                  >
                    {selectedAlert.alertName}
                  </Typography>
                  <Box
                    component="dl"
                    sx={{
                      m: 0,
                      display: "grid",
                      gridTemplateColumns: "88px 1fr",
                      gap: 0.5,
                      rowGap: 0.75,
                    }}
                  >
                    {[
                      [
                        "When",
                        (() => {
                          const wd = parseAlertApiDateTime(
                            selectedAlert.timestamp,
                          );
                          return Number.isNaN(wd.getTime())
                            ? "—"
                            : wd.toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                              });
                        })(),
                      ],
                      ["Camera", selectedAlert.sourceName || "—"],
                      ["Zone", selectedAlert.zoneName || "—"],
                      [
                        "Alert status",
                        selectedAlert.workspaceStatus ||
                          selectedAlert.status ||
                          "—",
                      ],
                    ].map(([k, v]) => (
                      <React.Fragment key={k}>
                        <Typography
                          component="dt"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 13,
                            color: "#6B7280",
                          }}
                        >
                          {k}
                        </Typography>
                        <Typography
                          component="dd"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 13,
                            color: "#212121",
                            m: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pr: thumbUiVisible ? 0.5 : 0,
                          }}
                        >
                          {v}
                        </Typography>
                      </React.Fragment>
                    ))}
                  </Box>

                  {showDetailsThumbnail && thumbUrl ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        if (!historyDetailThumbLoaded) return;
                        if (n >= 2 && thumbnailSlideIndex >= 0) {
                          setHistoryDetailCarouselIndex(thumbnailSlideIndex);
                          requestAnimationFrame(() => {
                            historyDetailMediaAreaRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          });
                        } else {
                          setHistoryThumbLightboxOpen(true);
                        }
                      }}
                      aria-label="Detection crop thumbnail — open in viewer"
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        right: 12,
                        zIndex: 2,
                        m: 0,
                        p: 0,
                        minWidth: { xs: 88, sm: 112 },
                        minHeight: { xs: 60, sm: 76 },
                        border:
                          n >= 2 &&
                          thumbnailSlideIndex >= 0 &&
                          carouselIndex === thumbnailSlideIndex
                            ? "2px solid #2932E5"
                            : "1px solid rgba(0,0,0,0.18)",
                        cursor: historyDetailThumbLoaded ? "pointer" : "default",
                        borderRadius: "10px",
                        bgcolor: "#fff",
                        boxShadow:
                          "0 2px 10px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)",
                        overflow: "hidden",
                        lineHeight: 0,
                        display: historyDetailThumbLoaded ? "block" : "none",
                        pointerEvents: historyDetailThumbLoaded ? "auto" : "none",
                        "&:focus-visible": {
                          outline: "2px solid #2932E5",
                          outlineOffset: 2,
                        },
                        "&:hover": { borderColor: "#2932E5" },
                      }}
                    >
                      <AlertHistoryMediaImage
                        href={thumbUrl}
                        alt="Detection thumbnail"
                        onLoad={() => setHistoryDetailThumbLoaded(true)}
                        onError={() => setHistoryDetailThumbLoaded(false)}
                        imgSx={{
                          display: "block",
                          width: { xs: 80, sm: 104 },
                          height: { xs: 56, sm: 72 },
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {historyThumbLightboxOpen &&
      selectedAlert?.thumbnailCropUrl &&
      historyAlertTypeUsesDetectionCropThumbnail(selectedAlert) ? (
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged thumbnail"
          onClick={() => setHistoryThumbLightboxOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 10050,
            bgcolor: "rgba(0,0,0,0.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1.5, sm: 3 },
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "relative",
              display: "inline-block",
              maxWidth: "min(96vw, 1400px)",
              maxHeight: "min(90vh, 900px)",
            }}
          >
            <IconButton
              onClick={() => setHistoryThumbLightboxOpen(false)}
              aria-label="Close enlarged image"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.55)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.72)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 22 }} />
            </IconButton>
            <AlertHistoryMediaImage
              href={selectedAlert.thumbnailCropUrl}
              alt=""
              imgSx={{
                display: "block",
                maxWidth: "min(96vw, 1400px)",
                maxHeight: "min(90vh, 900px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
              }}
            />
          </Box>
        </Box>
      ) : null}

      {/* Custom Date Range Picker Dialog */}
      <Dialog
        open={customDateDialog}
        onClose={handleCancelCustomRange}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            p: 2,
            pb: 1,
            backgroundColor: "white",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            sx={{
              lineHeight: "1.6",
              letterSpacing: "0.0075em",

              flex: "0 0 auto",
              fontFamily: "Roboto, sans-serif",
              fontSize: "20px",
              color: "rgb(255, 133, 85)",
              fontWeight: 600,
            }}
          >
            Custom Date Range
          </Typography>
        </DialogTitle>

        {/* <DialogContent sx={{ p: 2, backgroundColor: "white" }}> */}
        <DialogContent sx={{ p: "10px", backgroundColor: "#F2F2F2" }}>
          {/* Date Input Fields */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, mt: "10px" }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                placeholder="mm/dd/yyyy"
                value={
                  tempStartDate
                    ? tempStartDate.toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })
                    : ""
                }
                onChange={(e) => {
                  // Parse mm/dd/yyyy format
                  const dateStr = e.target.value;
                  const dateMatch = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                  if (dateMatch) {
                    const [, month, day, year] = dateMatch;
                    const date = new Date(year, month - 1, day);
                    if (!isNaN(date.getTime())) {
                      setTempStartDate(date);
                      setViewingMonth(date);
                    }
                  } else if (dateStr === "") {
                    setTempStartDate(null);
                  }
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarIcon sx={{ color: "#4A5565" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Roboto, sans-serif",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                  "& .MuiInputBase-input": {
                    font: "inherit",
                    letterSpacing: "inherit",
                    color: "currentcolor",
                    border: "0px",
                    boxSizing: "content-box",
                    background: "none",
                    height: "1.4375em",
                    margin: "0px",
                    WebkitTapHighlightColor: "transparent",
                    display: "block",
                    minWidth: "0px",
                    width: "100%",
                    animationName: "mui-auto-fill-cancel",
                    animationDuration: "10ms",
                    padding: "16.5px 14px",
                  },
                }}
              />
              <Typography
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  lineHeight: 1.66,
                  letterSpacing: "0.03333em",
                  textAlign: "left",
                  marginTop: "5px",
                  ml: 1.5,
                }}
              >
                Start Date
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                placeholder="mm/dd/yyyy"
                value={
                  tempEndDate
                    ? tempEndDate.toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })
                    : ""
                }
                onChange={(e) => {
                  // Parse mm/dd/yyyy format
                  const dateStr = e.target.value;
                  const dateMatch = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                  if (dateMatch) {
                    const [, month, day, year] = dateMatch;
                    const date = new Date(year, month - 1, day);
                    if (!isNaN(date.getTime())) {
                      setTempEndDate(date);
                    }
                  } else if (dateStr === "") {
                    setTempEndDate(null);
                  }
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarIcon sx={{ color: "#4A5565" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Roboto, sans-serif",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                  "& .MuiInputBase-input": {
                    font: "inherit",
                    letterSpacing: "inherit",
                    color: "currentcolor",
                    border: "0px",
                    boxSizing: "content-box",
                    background: "none",
                    height: "1.4375em",
                    margin: "0px",
                    WebkitTapHighlightColor: "transparent",
                    display: "block",
                    minWidth: "0px",
                    width: "100%",
                    animationName: "mui-auto-fill-cancel",
                    animationDuration: "10ms",
                    padding: "16.5px 14px",
                  },
                }}
              />
              <Typography
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  lineHeight: 1.66,
                  letterSpacing: "0.03333em",
                  textAlign: "left",
                  margin: 0,
                  ml: 1.5,
                }}
              >
                End Date
              </Typography>
            </Box>
          </Box>

          {/* Calendar View */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              p: 2,
            }}
          >
            {/* Month Navigation */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                pb: 1.5,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <IconButton
                onClick={() => {
                  const prevMonth = new Date(viewingMonth);
                  prevMonth.setMonth(prevMonth.getMonth() - 1);
                  setViewingMonth(prevMonth);
                }}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  width: "36px",
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "rgba(41, 50, 229, 0.08)",
                    borderColor: "#2932E5",
                  },
                }}
              >
                <KeyboardArrowLeftIcon sx={{ color: "#4A5565" }} />
              </IconButton>

              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#2932E5",
                }}
              >
                {viewingMonth.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>

              <IconButton
                onClick={() => {
                  const nextMonth = new Date(viewingMonth);
                  nextMonth.setMonth(nextMonth.getMonth() + 1);
                  setViewingMonth(nextMonth);
                }}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  width: "36px",
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "rgba(41, 50, 229, 0.08)",
                    borderColor: "#2932E5",
                  },
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    color: "#4A5565",
                    transform: "rotate(-90deg)",
                  }}
                />
              </IconButton>
            </Box>

            {/* Calendar Grid */}
            <Box>
              {/* Day Headers */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <Typography
                      key={day}
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#4A5565",
                        textAlign: "center",
                        py: 1,
                      }}
                    >
                      {day}
                    </Typography>
                  )
                )}
              </Box>

              {/* Calendar Days */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 0.5,
                }}
              >
                {(() => {
                  const year = viewingMonth.getFullYear();
                  const month = viewingMonth.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startDayOfWeek = firstDay.getDay();

                  const days = [];
                  // Empty cells for days before month starts
                  for (let i = 0; i < startDayOfWeek; i++) {
                    days.push(
                      <Box
                        key={`empty-${i}`}
                        sx={{
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    );
                  }

                  // Days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month, day);
                    const isToday =
                      date.toDateString() === new Date().toDateString();
                    const isStartDate =
                      tempStartDate &&
                      date.toDateString() === tempStartDate.toDateString();
                    const isEndDate =
                      tempEndDate &&
                      date.toDateString() === tempEndDate.toDateString();
                    const isSelected = isStartDate || isEndDate;
                    const isInRange =
                      tempStartDate &&
                      tempEndDate &&
                      date >= tempStartDate &&
                      date <= tempEndDate;

                    days.push(
                      <Box
                        key={day}
                        onClick={() => {
                          if (
                            !tempStartDate ||
                            (tempStartDate && tempEndDate)
                          ) {
                            // Reset selection or start new selection
                            setTempStartDate(date);
                            setTempEndDate(null);
                          } else if (date < tempStartDate) {
                            // Start date is after clicked date, swap them
                            setTempEndDate(tempStartDate);
                            setTempStartDate(date);
                          } else {
                            // Set end date
                            setTempEndDate(date);
                          }
                        }}
                        sx={{
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? "#FFFFFF"
                            : isToday
                            ? "#2932E5"
                            : "#212121",
                          backgroundColor: isSelected
                            ? "#2932E5"
                            : isInRange
                            ? "rgba(41, 50, 229, 0.1)"
                            : isToday
                            ? "rgba(41, 50, 229, 0.08)"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "#2932E5"
                              : "rgba(41, 50, 229, 0.1)",
                          },
                        }}
                      >
                        {day}
                      </Box>
                    );
                  }

                  return days;
                })()}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 2,
            backgroundColor: "white",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => {
                setTempStartDate(null);
                setTempEndDate(null);
              }}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Clear
            </Button>
            <Button
              onClick={() => {
                const today = new Date();
                setTempStartDate(today);
                setTempEndDate(today);
                setViewingMonth(today);
              }}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Today
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleCancelCustomRange}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyCustomRange}
              variant="contained"
              disabled={!tempStartDate || !tempEndDate}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                backgroundColor: "#2932E5",
                borderRadius: "8px",
                px: 3,
                "&:hover": {
                  backgroundColor: "#1f28c7",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <AlertHistorySettings
        open={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
        onSave={handleSettingsSave}
      />

      <Menu
        anchorReference={
          historyRowMenuPointerPosition ? "anchorPosition" : "anchorEl"
        }
        anchorEl={historyRowMenuAnchor}
        anchorPosition={historyRowMenuPointerPosition ?? undefined}
        open={Boolean(
          historyRowMenuAnchor || historyRowMenuPointerPosition,
        )}
        onClose={closeHistoryRowMenu}
        anchorOrigin={
          historyRowMenuPointerPosition
            ? { vertical: "top", horizontal: "left" }
            : { vertical: "bottom", horizontal: "right" }
        }
        transformOrigin={
          historyRowMenuPointerPosition
            ? { vertical: "top", horizontal: "left" }
            : { vertical: "top", horizontal: "right" }
        }
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        <MenuItem
          onClick={handleHistoryRowAcknowledge}
          disabled={
            historyRowMenuAlert &&
            effectiveWorkspaceStatus(historyRowMenuAlert) !== "Active"
          }
        >
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" sx={{ color: "#2E7D32" }} />
          </ListItemIcon>
          Acknowledge
        </MenuItem>
        <MenuItem
          onClick={handleHistoryRowClose}
          disabled={
            historyRowMenuAlert &&
            effectiveWorkspaceStatus(historyRowMenuAlert) === "Closed"
          }
        >
          <ListItemIcon>
            <CheckCircleIcon fontSize="small" sx={{ color: "#7B1FA2" }} />
          </ListItemIcon>
          Close
        </MenuItem>
      </Menu>
    </Box>
  );
}
